import type { EuropeUsaImportResultDto } from '@/api/types';
// ─────────────────────────────────────────────────────────────────────────────
// REQ-096 — Hooks TanStack Query tipados del módulo de Reconocimiento.
//
// Cada hook expone estados reales (loading/error/empty) provistos por TanStack Query:
//   - loading → `isLoading` / `isPending`
//   - error   → `isError` + `error` (RecognitionApiError)
//   - empty   → `data.items.length === 0` (feed) o `data.length === 0` (catálogo)
// Las vistas placeholder consumen estos estados sin datos falsos.
// ─────────────────────────────────────────────────────────────────────────────

import {
  useQuery, useQueries, useMutation, useQueryClient, useInfiniteQuery,
  type UseQueryResult, type InfiniteData,
} from '@tanstack/react-query';
import { FRONTLINE_USERS_ADMIN_KEY } from '@/hooks/useFrontlineUsersAdmin';
import { useFundamentals } from '@/hooks/useFundamentals';
import { useFundamentalsForReporting } from '@/hooks/useFundamentalsForReporting';
import { useUserContext } from '@/contexts/UserContext';
import { applyOptimisticToggle } from '@/utils/reactions';
import {
  recognitionApi, userAdminApi, rulesApi, notificationsApi, recognitionReportsApi,
  catalogApi, moderationApi, winnersApi, campaignsApi, campaignExceptionsApi, gestionApi, recapApi,
  recapConfigId, userViewsApi,
  getRecognitionErrorStatus,
  NO_AUTH_TOKEN_STATUS,
  POINTS_ENABLED_GLOBAL,
  POINTS_VISIBLE_TO_EMPLOYEES,
} from './client';
import { PERMISSION_ORDER, PREFERENCE_SCOPE } from './types';
import type {
  RecognitionDto,
  RecognitionDetailDto,
  CampaignDto,
  CampaignPointsDto,
  CampaignSummaryDto,
  FundamentalDto,
  PagedResultDto,
  CreateRecognitionBody,
  RecognitionFeedParams,
  LocalizedText,
  StreakDto,
  BadgeDto,
  PeopleSearchParams,
  PersonSummary,
  ManagerStatusDto,
  UserSearchResponse,
  ReportRecognitionBody,
  UpdateRecognitionBody,
  PromoteRecognitionBody,
  ReactionSummaryDto,
  ReactionUsersDto,
  ToggleReactionBody,
  DailyHabitDto,
  AttitudeBehaviorDto,
  CampaignDocumentDto,
  WrappedDto,
  RecognitionBulkImportRowDto,
  RecognitionBulkImportResultDto,
  NotificationInboxResponse,
  InboxNotificationDto,
  NotificationSelfPreferenceDto,
  PreferenceScope,
  NotificationRowDto,
  SystemBroadcastRequest,
  SystemBroadcastResult,
  RecognitionPreferenceDto,
  SaveRecognitionPreferenceRequest,
  ResolvedPreferencesDto,
  TeamMetricsDto,
  RecognitionTeamParams,
  RecognitionKpiDto,
  RecognitionSummaryParams,
  AdminUserDto,
  UpdateUserRolesBody,
  PermissionKey,
  PermissionMap,
  AdminRoleKey,
  PointsConfigDto,
  PointsConfigSummaryDto,
  PointsSegment,
  PointsToggleDto,
  BusinessRuleDto,
  FrontlineCatalogs,
  OrgCatalogItem,
  FrontlineRegisterBody,
  FrontlineRegisterResult,
  FrontlineActivateBody,
  VerifyEmailResult,
  UpdateAvatarBody,
  FrontlineAvatar,
  UserProfile,
  LegalDocumentDto,
  LegalDocType,
  LegalAcceptanceStatus,
  LegalAcceptanceBody,
  LegalDocumentDraftDto,
  SaveLegalDocumentDraftBody,
  PublishLegalDocumentBody,
  DailyReportDto,
  CampaignReportDto,
  HistoryReportDto,
  CampaignLiveKpiDto,
  HeatmapCellDto,
  OnaGraphDto,
  GenerationalFilters,
  DailyFunnelFilter,
  GenerationalReportDto,
  GenerationalDistributionReportDto,
  LeaderMetricRowDto,
  LeadersListResponseDto,
  WinnerDto,
  WinnersPageDto,
  WinnerSearchParams,
  WinnersExportFormat,
  RecognitionReportParams,
  DocumentationPillDto,
  UpsertPillBody,
  ReportDto,
  ReportDetailDto,
  ModerationReportDto,
  BlockedWordDto,
  CountryLanguageMapDto,
  ModerationNotifyPreviewDto,
  BlockedWordBody,
  ModerationSettingsDto,
  UserViewSettingsDto,
  RecognitionRulesDto,
  RecognitionProgramSettingsDto,
  RegisterWinnerBody,
  ImportWinnersBody,
  WinnerImportResultDto,
  BulkImportUsersResultDto,
  RegistrationOu,
  FrontlineLoginBody,
  FrontlineLoginResult,
  CampaignEstadoVisible,
  CampaignStatus,
  CampaignApprovalDto,
  CampaignApprovalAction,
  CreateCampaignBody,
  UpdateCampaignBody,
  UploadCampaignDocumentInput,
  CampaignExceptionDto,
  CampaignSchedulesOverviewDto,
  ActiveCampaignExceptionDto,
  CampaignType,
  CreateCampaignExceptionBody,
  CampaignExceptionApprovalAction,
  CampaignExceptionListParams,
  CampaignExceptionDocumentDto,
  UploadCampaignExceptionDocumentInput,
  GestionSettingsDto,
  LandingBannerDto,
  AvatarViewSettingsDto,
  RecapEditionDto,
  RecapEditionSummaryDto,
  RecapSlideBackgroundDto,
  WrappedAvailabilityDto,
  AdminBadgeDto,
  StreakSettingsBody,
  UploadCorporateDocumentInput,
  UpdateCorporateDocumentBody,
  HeadcountSummaryDto,
} from './types';

/** Claves de query namespaced para invalidación selectiva. */
export const recognitionKeys = {
  all: ['recognition'] as const,
  feed: (params: RecognitionFeedParams) => ['recognition', 'feed', params] as const,
  detail: (id: string) => ['recognition', 'detail', id] as const,
  reactions: (recognitionId: string) => ['recognition', 'reactions', recognitionId] as const,
  reactionUsers: (recognitionId: string) => ['recognition', 'reactionUsers', recognitionId] as const,
  // Nota: el catálogo de Fundamentales ya NO tiene clave propia acá — `useRecognitionCatalog`
  // delega en `useFundamentals()` (ver `@/hooks/useFundamentals`, query key ['catalog','fundamentals']).
  activeCampaign: (ou: string) => ['recognition', 'campaign', 'active', ou] as const,
  nextBrillamos: (ou: string) => ['recognition', 'campaign', 'next-brillamos', ou] as const,
  campaignDocs: (campaignId: string) => ['recognition', 'campaign', 'docs', campaignId] as const,
  // Tarea 15 — documentos PROPIOS de una excepción ("Campañas por OU"). Namespace propio: no
  // comparte caché con `campaignDocs` (son dos endpoints y dos colecciones distintas).
  campaignExceptionDocs: (exceptionId: string) => ['recognition', 'campaign-exception', 'docs', exceptionId] as const,
  streak: (programa?: string | null) => ['recognition', 'streak', 'me', programa ?? 'Fundamentales'] as const,
  people: (params: PeopleSearchParams) => ['recognition', 'people', params] as const,
  // RF-130 F3 (H-07) — `fromMonth` va en la clave: dos titulares (o el mismo con y sin recorte por
  // ingreso a mitad de año) comparten el mismo `year` pero no las mismas cifras, así que compartir
  // entrada de caché entre `undefined` y `9` filtraría las cifras de uno hacia el overlay del otro.
  wrapped: (year: number | undefined, fromMonth?: number | null) =>
    ['recognition', 'wrapped', year ?? 'current', fromMonth ?? null] as const,
};

/** GET v1/recognitions — feed paginado. `enabled` sigue el mismo patrón que useMyStreak/useMyWrapped
 *  (permite diferir el fetch, p.ej. hasta conocer el campaignId de la campaña activa). */
export function useRecognitions(params: RecognitionFeedParams = {}, enabled = true) {
  return useQuery<PagedResultDto<RecognitionDto>>({
    queryKey: recognitionKeys.feed(params),
    queryFn: () => recognitionApi.getRecognitions(params),
    enabled,
  });
}

/** GET v1/recognitions/{id} — detalle con snapshots. */
export function useRecognitionDetail(id: string | undefined) {
  return useQuery<RecognitionDetailDto>({
    queryKey: recognitionKeys.detail(id ?? ''),
    queryFn: () => recognitionApi.getRecognitionById(id as string),
    enabled: Boolean(id),
  });
}

/**
 * POST v1/recognitions — crea un reconocimiento e invalida el namespace `recognition` completo
 * (feed y racha incluidos) para que el muro y la racha se refresquen tras enviar (REQ-103).
 */
export function useCreateRecognition() {
  const qc = useQueryClient();
  return useMutation<RecognitionDto, unknown, CreateRecognitionBody>({
    mutationFn: (body: CreateRecognitionBody) => recognitionApi.createRecognition(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeys.all }),
  });
}

/**
 * GET v1/catalog/fundamentals (SurveyService) — catálogo único de Fundamentales.
 * P2-010: antes leía de RecognitionService (`GET v1/recognition-catalog`), colección propia que
 * quedó desincronizada de la de SurveyService (mismo `order`/`id` distintos, ver PENDIENTES.md).
 * SurveyService es la fuente que edita el admin de Pulso y la que ya usa `PerfilView` — unificar
 * aquí evita que `fundamentalIds` de un reconocimiento real no calce con lo que cuenta el perfil.
 *
 * Delega en `useFundamentals()` (mismo query key, misma normalización de `color`/`shortName` con
 * fallback determinista, mismo manejo de `USE_MOCK`) en vez de llamar `catalogApi.getFundamentals()`
 * directo: así ambos hooks comparten una sola entrada de caché de TanStack Query y nunca pueden
 * divergir en el color/shortName que le asignan a un mismo Fundamental sin esos campos en Mongo.
 */
export function useRecognitionCatalog(): UseQueryResult<FundamentalDto[]> {
  return useFundamentals();
}

/**
 * La MISMA lectura, pero incluyendo los Fundamentales dados de baja — delega en
 * `useFundamentalsForReporting()` igual que el de arriba delega en `useFundamentals()`, con su
 * propia entrada de caché.
 *
 * 🔴 Para qué existe la distinción (UAT, «Top Fundamental no aparece en Mi equipo»): una pantalla que
 * resuelve el NOMBRE de un fundamental ya usado en el pasado está leyendo HISTÓRICO, y el catálogo
 * activo no lo contiene si el admin lo dio de baja después. El `find` falla, y la columna cae al «—»
 * como si la persona no tuviera actividad — indistinguible de no tener datos. En el flujo de
 * CREACIÓN pasa lo contrario: ahí ofrecer un fundamental retirado sí sería un error, y por eso ese
 * flujo se queda con `useRecognitionCatalog`.
 */
export function useRecognitionCatalogForReporting(): UseQueryResult<FundamentalDto[]> {
  return useFundamentalsForReporting();
}

/**
 * GET v1/campaigns/active?ou= — campaña activa para una OU.
 * `data === null` significa 204: aplica el default de Fundamentales (no hay campaña activa).
 */
export function useActiveCampaign(ou: string | undefined) {
  return useQuery<CampaignDto | null>({
    queryKey: recognitionKeys.activeCampaign(ou ?? ''),
    queryFn: async () => (await recognitionApi.getActiveCampaign(ou as string)) ?? null,
    enabled: Boolean(ou),
  });
}

/** T5 — próxima campaña Brillamos programada a futuro para la OU (null = ninguna). Gobierna si la
 *  pantalla Reconocer ofrece el check "enviar también a Brillamos". */
export function useNextBrillamos(ou: string | undefined) {
  return useQuery<CampaignDto | null>({
    queryKey: recognitionKeys.nextBrillamos(ou ?? ''),
    queryFn: async () => (await recognitionApi.getNextBrillamos(ou as string)) ?? null,
    enabled: Boolean(ou),
  });
}

/**
 * GET v1/campaigns/{id}/documents — documentos de la campaña (WP-12 REQ-112).
 * Complementario: si el endpoint aún no está listo NO rompe la landing (retry 0, la UI usa estados).
 */
export function useCampaignDocuments(campaignId: string | undefined) {
  return useQuery<CampaignDocumentDto[]>({
    queryKey: recognitionKeys.campaignDocs(campaignId ?? ''),
    queryFn: () => recognitionApi.getCampaignDocuments(campaignId as string),
    enabled: Boolean(campaignId),
    staleTime: 10 * 60 * 1000,
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/wrapped — resumen anual del usuario (Wrapped).
 * Complementario: si el AnalyticsService aún no expone el endpoint no rompe el perfil (retry 0).
 *
 * RF-130 F3 (H-07) — `fromMonth` es el `desdeMes` de `GET v1/recap/availability` (mes de ingreso,
 * sólo con ingreso a mitad de año); `null`/`undefined` pide el año completo, igual que siempre. Los
 * DOS llamadores (`PerfilView`, `WrappedModal`) deben pasar el MISMO `year` y el MISMO `fromMonth`
 * para compartir esta misma entrada de caché — si divergen, el Recap dispara una petición extra por
 * cada apertura.
 */
export function useMyWrapped(year?: number, fromMonth?: number | null, enabled = true) {
  return useQuery<WrappedDto>({
    queryKey: recognitionKeys.wrapped(year, fromMonth),
    queryFn: () => recognitionApi.getMyWrapped(year, fromMonth),
    enabled,
    retry: 0,
  });
}

/** GET v1/streaks/me — racha del usuario (para el refresco tras enviar).
 *  Rama B — `programa` elige la racha (Fundamentales por defecto = el día a día). Va en la CLAVE de
 *  caché para que Fundamentales y Brillamos no compartan entrada (mostrarían la racha equivocada). */
export function useMyStreak(programa?: string | null, enabled = true) {
  return useQuery<StreakDto>({
    queryKey: recognitionKeys.streak(programa),
    queryFn: () => recognitionApi.getMyStreak(programa),
    enabled,
    // La racha no debe romper la vista si el endpoint aún no está listo.
    retry: 0,
  });
}

/**
 * Insignias de GANADORES (categoría Campaign) ya confirmadas — viaja dentro de `GET v1/streaks/me`
 * (`insigniasCampana`) pero se lee AQUÍ, del `useMyStreak` crudo, y no de `useVisibleStreak`: esa
 * puerta oculta el DTO entero cuando la racha está apagada (RF-043), y una medalla de Ganadores ya
 * otorgada no depende de ese interruptor — son dos funcionalidades distintas. Comparte la misma
 * entrada de caché que `useVisibleStreak`/`useMyStreak` (misma `queryKey`), así que usar los dos en
 * la misma pantalla no dispara una segunda petición.
 */
export function useMyCampaignBadges(enabled = true): BadgeDto[] {
  const streak = useMyStreak(undefined, enabled);
  return streak.data?.insigniasCampana ?? [];
}

/**
 * GET v1/users?search= — búsqueda de personas (receptores / "a nombre de").
 * Solo se ejecuta con al menos 2 caracteres, con debounce a cargo del componente.
 */
export function usePeopleSearch(params: PeopleSearchParams) {
  const term = params.search.trim();
  return useQuery<UserSearchResponse>({
    queryKey: recognitionKeys.people({ ...params, search: term }),
    queryFn: () => recognitionApi.searchPeople({ ...params, search: term }),
    enabled: term.length >= 2,
    staleTime: 60 * 1000,
  });
}

/** PATCH v1/recognitions/{id} — edita un reconocimiento e invalida el feed (REQ-107). */
export function useUpdateRecognition() {
  const qc = useQueryClient();
  return useMutation<RecognitionDto, unknown, { id: string; body: UpdateRecognitionBody }>({
    mutationFn: ({ id, body }) => recognitionApi.updateRecognition(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeys.all }),
  });
}

/** DELETE v1/recognitions/{id} — elimina un reconocimiento e invalida el feed (REQ-107). */
export function useDeleteRecognition() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id: string) => recognitionApi.deleteRecognition(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeys.all }),
  });
}

/** POST v1/recognitions/{id}/report — reporta e invalida el feed (queda oculto al revisar). */
export function useReportRecognition() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, { id: string; body: ReportRecognitionBody }>({
    mutationFn: ({ id, body }) => recognitionApi.reportRecognition(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeys.all }),
  });
}

/** POST v1/recognitions/{id}/promote — promueve diario → Brillamos e invalida el feed. */
export function usePromoteRecognition() {
  const qc = useQueryClient();
  return useMutation<RecognitionDto, unknown, { id: string; body: PromoteRecognitionBody }>({
    mutationFn: ({ id, body }) => recognitionApi.promoteRecognition(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeys.all }),
  });
}

/** GET v1/recognitions/{id}/reactions — resumen de reacciones del post-it. */
export function useReactions(recognitionId: string) {
  return useQuery<ReactionSummaryDto>({
    queryKey: recognitionKeys.reactions(recognitionId),
    queryFn: () => recognitionApi.getReactions(recognitionId),
    enabled: Boolean(recognitionId),
  });
}

/**
 * GET v1/recognitions/{id}/reactions/users — quiénes reaccionaron, con nombre. LAZY: solo dispara
 * cuando `enabled` es true (al abrir la lista), para no resolver nombres por cada tarjeta del muro.
 */
export function useReactionUsers(recognitionId: string, enabled: boolean) {
  return useQuery<ReactionUsersDto>({
    queryKey: recognitionKeys.reactionUsers(recognitionId),
    queryFn: () => recognitionApi.getReactionUsers(recognitionId),
    enabled: enabled && Boolean(recognitionId),
  });
}

/**
 * POST v1/recognitions/{id}/reactions — toggle de la reacción propia, con optimistic update:
 * aplica el cambio local al instante (`applyOptimisticToggle`) y revierte si el servidor falla.
 */
export function useToggleReaction() {
  const queryClient = useQueryClient();
  return useMutation<
    ReactionSummaryDto,
    unknown,
    { id: string; emoji: string },
    { previous: ReactionSummaryDto | undefined }
  >({
    mutationFn: ({ id: recognitionId, emoji }) =>
      recognitionApi.toggleReaction(recognitionId, { emoji } satisfies ToggleReactionBody),
    onMutate: async ({ id: recognitionId, emoji }) => {
      await queryClient.cancelQueries({ queryKey: recognitionKeys.reactions(recognitionId) });
      const previous = queryClient.getQueryData<ReactionSummaryDto>(recognitionKeys.reactions(recognitionId));
      queryClient.setQueryData<ReactionSummaryDto>(
        recognitionKeys.reactions(recognitionId),
        applyOptimisticToggle(previous, emoji),
      );
      return { previous };
    },
    onError: (_err, { id: recognitionId }, context) => {
      if (context?.previous === undefined) {
        queryClient.removeQueries({ queryKey: recognitionKeys.reactions(recognitionId) });
      } else {
        queryClient.setQueryData(recognitionKeys.reactions(recognitionId), context.previous);
      }
    },
    onSettled: (_data, _err, { id: recognitionId }) => {
      queryClient.invalidateQueries({ queryKey: recognitionKeys.reactions(recognitionId) });
    },
  });
}

// ─── Helpers de presentación ─────────────────────────────────────────────────

/** Resuelve un texto multiidioma con fallback es → en → primera clave disponible. */
export function pickLocalized(text: LocalizedText | null | undefined, lang: string): string {
  if (!text) return '';
  return text[lang] ?? text['es'] ?? text['en'] ?? Object.values(text)[0] ?? '';
}

/** Nombre de un Fundamental en el idioma dado con fallback. */
export function getFundamentalName(f: FundamentalDto, lang: string): string {
  return pickLocalized(f.names, lang) || f.shortName || f.code || f.id;
}

/** Nombre de una campaña en el idioma dado con fallback. */
export function getCampaignName(c: CampaignDto, lang: string): string {
  return pickLocalized(c.nombre, lang) || c.landingSlug || c.id;
}

/** Nombre de un daily habit en el idioma dado con fallback. */
export function getHabitName(h: DailyHabitDto, lang: string): string {
  return pickLocalized(h.names, lang) || h.code || h.id;
}

/** Nombre de una actitud/comportamiento en el idioma dado con fallback. */
export function getBehaviorName(b: AttitudeBehaviorDto, lang: string): string {
  return pickLocalized(b.names, lang) || b.code || b.id;
}

/**
 * Nombre de una insignia en el idioma dado con fallback. Los nombres son configurables desde
 * Gestión (RF-038), así que esta es la ÚNICA fuente: ninguna pantalla debe rotularlas por código.
 */
export function getBadgeName(b: BadgeDto, lang: string): string {
  return pickLocalized(b.nombre, lang) || b.code || b.id;
}

/**
 * Paleta cultural de Sigma como fallback determinista para el color de un Fundamental cuando el
 * catálogo no trae `color`. El orden coincide con F01→F04 de la spec.
 */
export const FUNDAMENTAL_PALETTE = ['#F08521', '#342F82', '#E11F26', '#20B15A', '#40B8E9', '#EF5395'];

/** Color de un Fundamental: el del catálogo si existe, si no uno estable por índice de orden. */
export function getFundamentalColor(f: FundamentalDto, index = 0): string {
  return f.color || FUNDAMENTAL_PALETTE[index % FUNDAMENTAL_PALETTE.length];
}

/**
 * Daily habits disponibles para el envío según lo seleccionado:
 * - F01 (tiene actitudes): solo los hábitos cuyas `attitudeBehaviorId` está en `selectedBehaviorIds`.
 * - F02–F04 (sin actitudes): todos los hábitos activos del Fundamental.
 */
export function availableHabitsFor(
  f: FundamentalDto,
  selectedBehaviorIds: string[],
): DailyHabitDto[] {
  const active = (f.dailyHabits ?? []).filter((h) => h.isActive);
  const hasAttitudes = (f.attitudes?.length ?? 0) > 0;
  if (!hasAttitudes) return active;
  if (selectedBehaviorIds.length === 0) return [];
  return active.filter(
    (h) => h.attitudeBehaviorId != null && selectedBehaviorIds.includes(h.attitudeBehaviorId),
  );
}

// ─── Claves de query adicionales (D4 Pass-B) ────────────────────────────────────
export const recognitionKeysExt = {
  admins: () => ['recognition', 'admins'] as const,
  /**
   * Raíz de TODO lo de puntos. Apagar el kill-switch global cambia lo que devuelven la config, el
   * resumen y el toggle de cada OU y segmento, así que la invalidación tiene que colgar de aquí:
   * invalidar solo la clave del toggle dejaba las tablas mostrando un estado que ya no era cierto.
   */
  pointsRoot: () => ['recognition', 'points'] as const,
  pointsConfig: (ouId: string, segment: PointsSegment) =>
    ['recognition', 'points', 'config', ouId, segment] as const,
  pointsSummary: (ouId: string, segment: string) =>
    ['recognition', 'points', 'summary', ouId, segment] as const,
  pointsToggle: (ouId: string, segment: PointsSegment) =>
    ['recognition', 'points', 'toggle', ouId, segment] as const,
  pointsGlobalRule: () => ['recognition', 'points', 'global-rule'] as const,
  pointsVisibleRule: () => ['recognition', 'points', 'visible-rule'] as const,
  frontlineCatalogs: (ou: string) => ['recognition', 'frontline-catalogs', ou] as const,
  /** RF-008 — perfil del usuario (el único endpoint que trae `avatar`). */
  userProfile: (id: string) => ['recognition', 'user-profile', id] as const,
  legalStatus: () => ['recognition', 'legal', 'status'] as const,
  // `ouId` va al FINAL de la clave a propósito: las invalidaciones que ya existen pasan
  // `legalDoc(type, 'es')` y siguen alcanzando a todas las OUs por coincidencia de prefijo.
  legalDoc: (type: LegalDocType, lang: string, ouId?: string) =>
    ['recognition', 'legal', 'doc', type, lang, ouId ?? ''] as const,
  inbox: () => ['recognition', 'inbox'] as const,
  notifPreferences: () => ['recognition', 'notif-preferences', 'me'] as const,
  notifSettings: (scope: PreferenceScope, scopeId: string | null) =>
    ['recognition', 'notif-settings', scope, scopeId ?? ''] as const,
  team: (p: RecognitionTeamParams) => ['recognition', 'team', p] as const,
  summary: (p: RecognitionSummaryParams) => ['recognition', 'summary', p] as const,
  corporateDocs: () => ['recognition', 'corporate-docs'] as const,
  corporateDocsForMe: (lang: string) => ['recognition', 'corporate-docs', 'for-me', lang] as const,
};

// ─── WP-11 REQ-106 — Carga masiva CSV ───────────────────────────────────────────

interface BulkUploadVars {
  rows: RecognitionBulkImportRowDto[];
  campanaId: string | null;
  preview: boolean;
  /** Programa desde el que se carga (Brillamos lo manda explícito). Sin él, el servidor asume Fundamentales. */
  programa?: string | null;
}

/** POST v1/recognitions/bulk — valida (preview:true) o confirma (preview:false) una carga masiva. */
export function useBulkUpload() {
  const qc = useQueryClient();
  return useMutation<RecognitionBulkImportResultDto, unknown, BulkUploadVars>({
    mutationFn: ({ rows, campanaId, preview, programa }) =>
      recognitionApi.bulkUpload(rows, campanaId, preview, programa),
    onSuccess: (_data, vars) => {
      // Solo la confirmación real (no el preview) refresca el muro.
      if (!vars.preview) qc.invalidateQueries({ queryKey: recognitionKeys.all });
    },
  });
}

// ─── REQ-071 / RF-164 — Bandeja de notificaciones in-app (NotificationService) ──

/** Tamaño de página del inbox. El "ver más" acumula páginas de este tamaño. */
const INBOX_PAGE_SIZE = 20;

/**
 * GET v1/notifications/inbox — bandeja del usuario, PAGINADA de forma acumulativa (RF-164: historial
 * completo). Antes se pedía solo la primera página y no había forma de llegar a lo anterior.
 *
 * `enabled` se ata al feature flag VITE_FEATURE_RECOGNITION_INBOX. `retry: 0` para fallar rápido a un
 * estado honesto; refresco cada 60 s.
 */
export function useNotificationInbox(enabled: boolean) {
  return useInfiniteQuery<NotificationInboxResponse>({
    queryKey: recognitionKeysExt.inbox(),
    queryFn: ({ pageParam }) => notificationsApi.getInbox(pageParam as number, INBOX_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.page * last.pageSize < last.total ? last.page + 1 : undefined,
    enabled,
    retry: 0,
    // El sondeo se mantiene SIEMPRE: es la única fuente de avisos nuevos, y el contador es justo lo
    // que RF-164 pide mantener al día. La versión anterior lo apagaba al desplegar el historial para
    // ahorrar peticiones, pero la campana vive en el riel y nunca se desmonta: una vez apagado no
    // volvía a encenderse sin recargar la página, y el badge se congelaba para el resto de la sesión.
    //
    // El coste de refrescar N páginas se acota con `maxPages`: solo se conservan (y por tanto se
    // refrescan) las 5 últimas páginas cargadas, que a 20 por página son 100 avisos de historial.
    refetchInterval: enabled ? 60 * 1000 : false,
    maxPages: 5,
  });
}

/**
 * RF-164 — Aplica el cambio de estado de UN aviso a la caché ya descargada y recalcula el contador.
 *
 * Se exporta por ser la pieza que de verdad implementa "limpieza correcta del badge": es una función
 * pura y se prueba como tal, sin montar la campana ni simular la red.
 *
 * El badge se actualiza así, y no esperando al refetch: invalidar deja el contador con el valor viejo
 * mientras dura el viaje de red, de modo que el usuario marca como leído y el número no se mueve. Es
 * exactamente la "limpieza del badge" que pedía el requerimiento.
 */
export function applyReadState(
  prev: InfiniteData<NotificationInboxResponse> | undefined,
  id: string,
  isRead: boolean,
): InfiniteData<NotificationInboxResponse> | undefined {
  if (!prev) return prev;

  const found = prev.pages.some((page) =>
    page.items.some((item) => item.id === id && item.isRead !== isRead));
  if (!found) return prev;

  const delta = isRead ? -1 : 1;
  const setItem = (item: InboxNotificationDto) =>
    (item.id === id ? { ...item, isRead } : item);

  return {
    ...prev,
    pages: prev.pages.map((page) => ({
      ...page,
      // El contador no puede bajar de cero aunque la caché esté desincronizada con el servidor.
      unreadCount: Math.max(0, page.unreadCount + delta),
      items: page.items.map(setItem),
    })),
  };
}

function patchInboxCache(
  qc: ReturnType<typeof useQueryClient>,
  id: string,
  isRead: boolean,
) {
  qc.setQueryData<InfiniteData<NotificationInboxResponse>>(
    recognitionKeysExt.inbox(),
    (prev) => applyReadState(prev, id, isRead),
  );
}

/** POST v1/notifications/inbox/{id}/read — marca leído, con badge optimista y reconciliación. */
export function useMarkNotificationRead() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id: string) => notificationsApi.markRead(id),
    onMutate: async (id) => {
      // Cancelar ANTES de parchear es obligatorio: si el sondeo de 60 s tiene un GET en vuelo, su
      // respuesta —anterior al clic— sobreescribe la caché y el badge vuelve a subir con el sobre
      // marcado otra vez como no leído, con la mutación todavía viajando.
      await qc.cancelQueries({ queryKey: recognitionKeysExt.inbox() });
      patchInboxCache(qc, id, true);
    },
    // Si el servidor rechaza el cambio, el refetch devuelve la verdad: no se deja la pantalla
    // mintiendo sobre un estado que no se guardó.
    onSettled: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.inbox() }),
  });
}

/** POST v1/notifications/inbox/{id}/unread — vuelta atrás: devuelve el aviso a no leído (RF-164). */
export function useMarkNotificationUnread() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id: string) => notificationsApi.markUnread(id),
    onMutate: async (id) => {
      await qc.cancelQueries({ queryKey: recognitionKeysExt.inbox() });
      patchInboxCache(qc, id, false);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.inbox() }),
  });
}

/** Deja leídos cuantos avisos haya en la caché y pone el contador a cero. */
export function markEveryPageRead(
  prev: InfiniteData<NotificationInboxResponse> | undefined,
): InfiniteData<NotificationInboxResponse> | undefined {
  if (!prev) return prev;
  return {
    ...prev,
    pages: prev.pages.map((page) => ({
      ...page,
      unreadCount: 0,
      items: page.items.map(asRead),
    })),
  };
}

const asRead = (item: InboxNotificationDto) => ({ ...item, isRead: true });

/** POST v1/notifications/inbox/read-all — limpia el badge de golpe. */
export function useMarkAllNotificationsRead() {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, unknown, void>({
    mutationFn: () => notificationsApi.markAllRead(),
    onMutate: async () => {
      await qc.cancelQueries({ queryKey: recognitionKeysExt.inbox() });
      qc.setQueryData<InfiniteData<NotificationInboxResponse>>(
        recognitionKeysExt.inbox(), markEveryPageRead);
    },
    onSettled: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.inbox() }),
  });
}

// ─── REQ-070 — Preferencias self-service de notificaciones ───────────────────────

/** GET v1/notifications/recognition/me/preferences — preferencias del usuario. retry 0 (estado honesto). */
export function useNotificationPreferences(enabled = true) {
  return useQuery<NotificationSelfPreferenceDto>({
    queryKey: recognitionKeysExt.notifPreferences(),
    queryFn: () => notificationsApi.getSelfPreferences(),
    enabled,
    retry: 0,
  });
}

/** PUT v1/notifications/recognition/me/preferences — guarda las preferencias e invalida la consulta. */
export function useSaveNotificationPreferences() {
  const qc = useQueryClient();
  return useMutation<void, unknown, NotificationSelfPreferenceDto>({
    mutationFn: (body) => notificationsApi.saveSelfPreferences(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.notifPreferences() }),
  });
}

// ─── RF-162 — Configuración jerárquica de notificaciones (admin) ────────────────

/**
 * GET v1/notifications/recognition/settings — documento crudo del nivel seleccionado. `retry: 0` para
 * caer a un estado honesto (la pantalla muestra "no disponible") en vez de reintentar contra un
 * servicio caído y dejar los toggles en un estado que no es el guardado.
 *
 * Se deshabilita si el nivel es acotado y aún no se eligió un id: el backend responde 400 a propósito
 * y no tiene sentido preguntarle.
 */
export function useRecognitionSettings(scope: PreferenceScope, scopeId: string | null) {
  const needsId = scope !== PREFERENCE_SCOPE.Global;
  return useQuery<RecognitionPreferenceDto>({
    queryKey: recognitionKeysExt.notifSettings(scope, scopeId),
    queryFn: () => notificationsApi.getRecognitionSettings(scope, scopeId),
    enabled: !needsId || Boolean(scopeId),
    retry: 0,
  });
}

/**
 * WP-F4-b (6.3) — GET .../recognition/rows — las filas de las dos tablas del nivel seleccionado.
 *
 * Misma disciplina que la consulta hermana: `retry: 0` para caer a un estado honesto en vez de
 * reintentar contra un servicio caído, y deshabilitada mientras un nivel acotado no tenga id —el
 * backend responde 400 a propósito y no tiene sentido preguntarle—.
 */
export function useRecognitionRows(scope: PreferenceScope, scopeId: string | null) {
  const needsId = scope !== PREFERENCE_SCOPE.Global;
  return useQuery<NotificationRowDto[]>({
    queryKey: ['recognition', 'notif-rows', scope, scopeId ?? null],
    queryFn: () => notificationsApi.getRecognitionRows(scope, scopeId),
    enabled: !needsId || Boolean(scopeId),
    retry: 0,
  });
}

/**
 * WP-F4-b (6.3) — PUT .../rows/{key}/subject — el asunto de una fila.
 *
 * Invalida TODAS las consultas de filas, no solo la del nivel actual: el asunto de una plantilla es
 * del tenant, no del nivel —lo que cambia por nivel son los interruptores—, así que dejar el resto
 * en caché enseñaría el asunto viejo al cambiar de OU.
 */
export function useSaveRecognitionRowSubject() {
  const qc = useQueryClient();
  return useMutation<{ updated: number }, unknown, { templateKey: string; subjects: Record<string, string> }>({
    mutationFn: ({ templateKey, subjects }) =>
      notificationsApi.saveRecognitionRowSubject(templateKey, subjects),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'notif-rows'] });
    },
  });
}

/**
 * WP-F4-b (6.4) — POST .../recognition/broadcast. No invalida nada: un aviso del sistema no cambia
 * ninguna configuración, solo sale.
 */
export function useSendSystemBroadcast() {
  return useMutation<SystemBroadcastResult, unknown, SystemBroadcastRequest>({
    mutationFn: (body) => notificationsApi.sendSystemBroadcast(body),
  });
}

/** PUT v1/notifications/recognition/settings — guarda el nivel e invalida su consulta y la resuelta. */
export function useSaveRecognitionSettings() {
  const qc = useQueryClient();
  return useMutation<void, unknown, SaveRecognitionPreferenceRequest>({
    mutationFn: (body) => notificationsApi.saveRecognitionSettings(body),
    onSuccess: (_data, body) => {
      qc.invalidateQueries({
        queryKey: recognitionKeysExt.notifSettings(body.scope, body.scopeId ?? null),
      });
      // La vista previa de configuración efectiva depende de cada nivel, no solo del que se guardó.
      qc.invalidateQueries({ queryKey: ['recognition', 'notif-settings-resolved'] });
    },
  });
}

/**
 * GET v1/notifications/recognition/settings/resolve — configuración EFECTIVA de un scope, para
 * previsualizar en el panel qué recibe de verdad alguien de ese nivel una vez aplicada la precedencia.
 */
export function useResolvedRecognitionSettings(
  params: { siteId?: string | null; buId?: string | null; ouId?: string | null },
  enabled = true,
) {
  return useQuery<ResolvedPreferencesDto>({
    queryKey: ['recognition', 'notif-settings-resolved', params] as const,
    queryFn: () => notificationsApi.resolveRecognitionSettings(params),
    enabled,
    retry: 0,
  });
}

// ─── REQ-121 — Resolución de nombre por id (Wrapped: top receptor/emisor) ────────

/** GET v1/users/{id} — resuelve el nombre de una persona desde el directorio. retry 0, degrada a '—'. */
export function useUserById(id: string | null | undefined) {
  return useQuery<PersonSummary>({
    queryKey: ['recognition', 'user', id ?? ''],
    queryFn: () => userAdminApi.getUserById(id as string),
    enabled: Boolean(id),
    retry: 0,
    staleTime: 10 * 60 * 1000,
  });
}

// ─── Mi Equipo — REQ-086 ──────────────────────────────────────────────────

/** GET v1/users/{id}/manager-status — ¿tiene reportes directos? Gatea el botón "Equipo" del Perfil
 *  y el drill-down de la tabla de equipo. retry 0, sin id no dispara la consulta. */
export function useManagerStatus(userId: string | null | undefined) {
  return useQuery<ManagerStatusDto>({
    queryKey: ['recognition', 'manager-status', userId ?? ''],
    queryFn: () => userAdminApi.getManagerStatus(userId as string),
    enabled: Boolean(userId),
    retry: 0,
    staleTime: 10 * 60 * 1000,
  });
}

// ─── WP-15 REQ-127 — Roles y Permisos ───────────────────────────────────────────

/** Todos los permisos en `false` — estado inicial antes de elegir un rol al agregar un admin. */
export const ALL_FALSE: PermissionMap = PERMISSION_ORDER.reduce(
  (acc, k) => { acc[k] = false; return acc; },
  {} as PermissionMap,
);

/** Construye un PermissionMap a partir de las claves habilitadas. */
function permsFrom(keys: PermissionKey[]): PermissionMap {
  const map = { ...ALL_FALSE };
  for (const k of keys) map[k] = true;
  return map;
}

/**
 * Plantillas de permisos por rol (cascada RN-R01). Al seleccionar un rol se aplican estos permisos;
 * el admin puede anular permisos individuales después. El backend revalida (autorización siempre server-side).
 */
export const ROLE_PERMISSION_TEMPLATES: Record<AdminRoleKey, PermissionMap> = {
  // Espejo de PMP.SharedContracts.Authorization.RolePermissionMatrix — mantener sincronizado a mano.
  AdminGlobal: permsFrom(PERMISSION_ORDER.filter((k) => k !== 'readOnly')),
  AdminBrillamos: permsFrom([
    'manageRoles', 'managePointsConfig', 'manageRecognitions', 'moderateContent',
    'manageBlockedWords', 'manageWinners', 'manageCampaigns', 'createCampaigns', 'viewReports',
    'managePoints', 'manageNotifications', 'configureViews', 'manageDocuments', 'manageRecap',
    'viewAnalytics', 'exportData', 'bulkCsv', 'approveNominations',
  ]),
  AdminOUBrillamos: permsFrom(['viewReports']),
  // Admin OU Pulso: nace con Reportería (única excepción, decisión de negocio 2026-09-25 — ver
  // REPORTERIA_GUARANTEED_ROLES en auth/accessMatrix.ts y RolePermissionMatrix.AdminOuPulsoSet en
  // SharedContracts) y el resto de casillas apagadas. Reportería queda garantizada por ROL: aunque
  // el operador la desmarque aquí, `canAccessRecognitionAdminSection` la sigue concediendo — el
  // checkbox documenta el estado de fábrica, no controla esta capacidad para este rol. El resto de
  // permisos sí son deliberados uno por uno, como «Gestión» (managePoints).
  AdminOU: permsFrom(['viewReports']),
};

/** Devuelve la plantilla de permisos (cascada) para un rol. */
export function permissionsForRole(role: AdminRoleKey): PermissionMap {
  return { ...ROLE_PERMISSION_TEMPLATES[role] };
}

/** GET v1/users/recognition-admins — administradores del módulo (tabla de Roles y Permisos). */
export function useAdmins() {
  return useQuery<AdminUserDto[]>({
    queryKey: recognitionKeysExt.admins(),
    queryFn: () => userAdminApi.getAdmins(),
  });
}

/** PUT v1/users/{id}/recognition-role — guarda rol/alcance/estado/permisos e invalida la lista de admins. */
export function useUpdateUserRoles() {
  const qc = useQueryClient();
  return useMutation<AdminUserDto, unknown, { id: string; body: UpdateUserRolesBody }>({
    mutationFn: ({ id, body }) => userAdminApi.updateUserRoles(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'admins'] }),
  });
}

// ─── WP-15 REQ-128 — Configuración de Puntos ────────────────────────────────────

/** GET v1/rules/points-config/{ouId}?segment= — config de puntos de la OU por segmento. */
export function usePointsConfig(ouId: string | undefined, segment: PointsSegment) {
  return useQuery<PointsConfigDto>({
    queryKey: recognitionKeysExt.pointsConfig(ouId ?? '', segment),
    queryFn: () => rulesApi.getPointsConfig(ouId as string, segment),
    enabled: Boolean(ouId),
  });
}

/** PUT v1/rules/points-config/{ouId}?segment= — guarda la config (bloqueada si CampaignStarted). */
export function useSavePointsConfig() {
  const qc = useQueryClient();
  return useMutation<PointsConfigDto, unknown, { ouId: string; segment: PointsSegment; body: PointsConfigDto }>({
    mutationFn: ({ ouId, segment, body }) => rulesApi.savePointsConfig(ouId, segment, body),
    // Invalida TODA la rama de puntos, no solo la config: guardar pointsEnabled cambia lo que
    // devuelven el toggle resuelto y —cuando el motor vuelva a acreditar— el resumen. Invalidar
    // solo la config dejaba el interruptor de la tabla y la columna mostrando el estado anterior.
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.pointsRoot() }),
  });
}

/** POST v1/rules/points-config/{ouId}/reset?segment= — re-establece a 0 el Total Acumulado. */
export function useResetPointsConfigAccumulated() {
  const qc = useQueryClient();
  return useMutation<PointsConfigDto, unknown, { ouId: string; segment: PointsSegment }>({
    mutationFn: ({ ouId, segment }) => rulesApi.resetPointsConfig(ouId, segment),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.pointsRoot() }),
  });
}

/**
 * WP-A4 / RF-039 — GET v1/rules/points-config/summary: el conteo de nominaciones por criterio de la
 * columna "Total Acumulado".
 *
 * Va SIN `ouId` cuando el admin no ha elegido ninguna: el backend resuelve el alcance desde los
 * claims (global para AdminGlobal, la propia OU para el resto), así que aquí no se decide nada.
 */
export function usePointsSummary(ouId: string | undefined, segment: PointsSegment | undefined) {
  return useQuery<PointsConfigSummaryDto>({
    queryKey: recognitionKeysExt.pointsSummary(ouId ?? '', segment ?? ''),
    queryFn: () => rulesApi.getPointsSummary(ouId, segment),
  });
}

/** WP-A3 / REQ-038 — estado resuelto del interruptor de puntos para una OU/segmento. */
export function usePointsToggle(ouId: string | undefined, segment: PointsSegment) {
  return useQuery<PointsToggleDto>({
    queryKey: recognitionKeysExt.pointsToggle(ouId ?? '', segment),
    queryFn: () => rulesApi.getPointsToggle(ouId as string, segment),
    enabled: Boolean(ouId),
  });
}

/**
 * WP-A3 — la regla global EN CRUDO, que es lo que el control edita.
 *
 * No depende de ninguna OU: es el valor base. Se separa del toggle resuelto a propósito, porque un
 * control que ESCRIBE el base y MUESTRA el resuelto miente en cuanto hay un override.
 */
export function usePointsGlobalRule() {
  return useQuery<BusinessRuleDto>({
    queryKey: recognitionKeysExt.pointsGlobalRule(),
    queryFn: () => rulesApi.getRule(POINTS_ENABLED_GLOBAL),
  });
}

/**
 * WP-A3 / D-1 — enciende o apaga el kill-switch GLOBAL de puntos.
 *
 * Invalida TODO lo de puntos, no solo el toggle: apagarlo cambia lo que devuelven la config, el
 * resumen y el toggle de cada OU y segmento. Invalidar solo la clave del toggle dejaba las tablas
 * mostrando un estado que ya no era cierto.
 */
export function useSetGlobalPointsEnabled() {
  const qc = useQueryClient();
  return useMutation<unknown, unknown, boolean>({
    mutationFn: (enabled) => rulesApi.setGlobalPointsEnabled(enabled),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recognitionKeysExt.pointsRoot() });
    },
  });
}

/**
 * WP-A5 · RN-P09 — la regla «Visible para empleados» EN CRUDO, con su valor base y sus overrides.
 *
 * Se pide en crudo por el mismo motivo que la global: el control edita el valor **de una OU**, y
 * para saber qué pintar hay que ver si esa OU tiene override propio o hereda el base. El valor ya
 * RESUELTO —con el suelo duro de GDPR aplicado— llega por otro lado (`PointsConfigDto`), y sirve
 * para avisar, no para pintar el interruptor.
 */
export function usePointsVisibleRule() {
  return useQuery<BusinessRuleDto>({
    queryKey: recognitionKeysExt.pointsVisibleRule(),
    queryFn: () => rulesApi.getRule(POINTS_VISIBLE_TO_EMPLOYEES),
  });
}

/**
 * WP-A5 — enciende o apaga «Visible para empleados» PARA UNA OU, con un override.
 *
 * 🔴 Siempre escribe un override explícito (`'true'` / `'false'`), nunca `null`. `null` BORRARÍA el
 * override y la OU volvería a heredar el valor base, que no es lo que el admin pidió al mover un
 * interruptor: pedir «apagado» tiene que quedar escrito como apagado, no como «lo que diga el
 * global». Es la misma distinción que ya confundió al kill-switch global una vez.
 *
 * Invalida TODO lo de puntos: el flag resuelto viaja en `PointsConfigDto`, así que la tabla de
 * criterios y el resumen también dejan de ser ciertos al cambiarlo.
 */
export function useSetOuPointsVisibility() {
  const qc = useQueryClient();
  return useMutation<unknown, unknown, { ouId: string; visible: boolean }>({
    mutationFn: ({ ouId, visible }) =>
      rulesApi.setOuRuleOverride(POINTS_VISIBLE_TO_EMPLOYEES, ouId, String(visible)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: recognitionKeysExt.pointsRoot() });
      qc.invalidateQueries({ queryKey: recognitionKeysExt.pointsVisibleRule() });
    },
  });
}

// ─── WP-16 REQ-139 — Registro standalone Frontline ──────────────────────────────

/** GET v1/users/org-catalogs?ou= — catálogos BU/site/depto para el registro (NO hardcodeados). */
export function useFrontlineCatalogs(ou: string | undefined) {
  return useQuery<FrontlineCatalogs>({
    queryKey: recognitionKeysExt.frontlineCatalogs(ou ?? ''),
    queryFn: () => userAdminApi.getFrontlineCatalogs(ou as string),
    enabled: Boolean(ou),
    staleTime: 10 * 60 * 1000,
  });
}

/** GET v1/users/registration-catalogs — OUs públicas para el registro (id real + bandera Europa). */
export function useRegistrationCatalogs() {
  return useQuery<RegistrationOu[]>({
    queryKey: ['recognition', 'registration-catalogs'],
    queryFn: () => userAdminApi.getRegistrationCatalogs(),
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Fase 6 (Reportería) — GET v1/users/sites. Catálogo maestro de Sites para el selector "Site" del
 * filtro (Diario/Brillamos/Demográfico). 2026-09-24 — con `ouIds` (OUs ELEGIDAS, sin expandir) la lista
 * se acota a los Sites de las personas de esas OUs; un Admin de OU nunca recibe Sites fuera de su
 * alcance (lo resuelve el backend). `staleTime` largo a propósito: es un catálogo organizativo, cambia
 * con la sincronización de SuccessFactors, no con la interacción del usuario.
 */
export function useSites(ouIds: readonly string[] = [], enabled = true) {
  // La selección entra en la clave ORDENADA: elegir las mismas OUs en otro orden no debe pedir la
  // lista otra vez.
  const clave = [...ouIds].sort((a, b) => a.localeCompare(b));
  return useQuery<OrgCatalogItem[]>({
    queryKey: ['recognition', 'sites', clave],
    queryFn: () => userAdminApi.getSites(clave),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/**
 * 2026-10-06 — GET v1/users/business-units. BUs de las OUs ELEGIDAS para el selector «BU» de
 * Reportería › Demográfico: al cambiar la OU, la lista se recalcula con las BUs de esa OU. Mismo
 * criterio de clave y de caché que `useSites`.
 */
export function useBusinessUnits(ouIds: readonly string[] = [], enabled = true) {
  const clave = [...ouIds].sort((a, b) => a.localeCompare(b));
  return useQuery<OrgCatalogItem[]>({
    queryKey: ['recognition', 'business-units', clave],
    queryFn: () => userAdminApi.getBusinessUnits(clave),
    enabled,
    staleTime: 10 * 60 * 1000,
  });
}

/** POST v1/users/frontline-register — alta de un colaborador de línea. */
export function useFrontlineRegister() {
  return useMutation<FrontlineRegisterResult, unknown, FrontlineRegisterBody>({
    mutationFn: (body: FrontlineRegisterBody) => userAdminApi.frontlineRegister(body),
  });
}

/** POST v1/users/frontline-login — login standalone por contraseña (emite JWT propio). */
export function useFrontlineLogin() {
  return useMutation<FrontlineLoginResult, unknown, FrontlineLoginBody>({
    mutationFn: (body: FrontlineLoginBody) => userAdminApi.frontlineLogin(body),
  });
}

/** POST v1/users/frontline-activate — el colaborador cargado por bulk fija su contraseña (activación). */
export function useFrontlineActivate() {
  return useMutation<FrontlineRegisterResult, unknown, FrontlineActivateBody>({
    mutationFn: (body: FrontlineActivateBody) => userAdminApi.frontlineActivate(body),
  });
}

/** POST v1/users/bulk-import — carga masiva del padrón Frontline por CSV (RF-010/RF-114). */
export function useBulkImportUsers() {
  return useMutation<BulkImportUsersResultDto, unknown, string>({
    mutationFn: (csv: string) => userAdminApi.bulkImportUsers(csv),
  });
}

/** POST v1/users/europe-import — carga masiva del padrón Europa (AdminGlobal + tenant Europa). */
export function useEuropeImportUsers() {
  return useMutation<BulkImportUsersResultDto, unknown, string>({
    mutationFn: (csv: string) => userAdminApi.europeImportUsers(csv),
  });
}

/**
 * POST v1/users/europe-usa-import — carga masiva del padron de Europa y EE. UU. (PLAN WP-U6).
 * Con preview=true valida el archivo entero y NO escribe nada.
 */
export function useEuropeUsaImport() {
  const qc = useQueryClient();
  return useMutation<EuropeUsaImportResultDto, unknown, { csv: string; preview: boolean; ou: string }>({
    mutationFn: ({ csv, preview, ou }) => userAdminApi.europeUsaImport(csv, preview, ou),
    onSuccess: (_result, variables) => {
      // 🔴 Sólo cuando se ESCRIBIÓ. La vista previa no cambia nada, así que invalidar ahí sería
      // recargar el listado entero para nada — y encima daría a entender que ya se importó.
      if (variables.preview) return;

      qc.invalidateQueries({ queryKey: ['users'] });
      qc.invalidateQueries({ queryKey: recognitionKeys.all });
      // «Gestión de usuarios» lista a las personas recién cargadas con su propia clave, que no cuelga de ['users'].
      qc.invalidateQueries({ queryKey: FRONTLINE_USERS_ADMIN_KEY });
    },
  });
}

/** GET v1/users/europe-usa-import/template — la plantilla la sirve el backend (D-U6). */
export function useEuropeUsaTemplate() {
  return useMutation<string, unknown, void>({
    mutationFn: () => userAdminApi.europeUsaImportTemplate(),
  });
}

/**
 * POST v1/users/resend-verification — reenvía el correo de verificación.
 *
 * `isSuccess` significa «el servidor recibió la petición», NO «el correo salió»: la respuesta es un
 * 202 invariable. La pantalla enseña el mismo mensaje pase lo que pase, por diseño (R3.2).
 */
export function useResendVerification() {
  return useMutation<void, unknown, string>({
    mutationFn: (correo: string) => userAdminApi.resendVerification(correo),
  });
}

/** POST v1/users/forgot-password — pide el enlace de restablecimiento. 202 opaco, como el de arriba. */
export function useForgotPassword() {
  return useMutation<void, unknown, string>({
    mutationFn: (correo: string) => userAdminApi.forgotPassword(correo),
  });
}

/** POST v1/users/reset-password — fija la contraseña nueva. 204, o 400 genérico si el token no sirve. */
export function useResetPassword() {
  return useMutation<void, unknown, { token: string; password: string }>({
    mutationFn: ({ token, password }) => userAdminApi.resetPassword(token, password),
  });
}

/**
 * POST v1/users/verify-email — confirma la verificación del correo con el token en el cuerpo. Es una
 * MUTACIÓN y no una consulta a propósito: ocurre sólo cuando la persona pulsa «Confirmar mi correo», no al
 * abrir la pantalla, así que un antivirus o un cliente de correo que precargue el enlace no gasta el token.
 */
export function useVerifyEmailConfirm() {
  return useMutation<VerifyEmailResult, unknown, string>({
    mutationFn: (token: string) => userAdminApi.verifyEmailConfirm(token),
  });
}

/**
 * GET v1/users/sso-domains — los dominios de correo con los que se entra por SSO (lista propia del registro). Anónimo y estable:
 * una hora de caché, como el catálogo de organizaciones. Si falla, el formulario NO se bloquea (el
 * servidor sigue rechazando el correo corporativo); sólo se pierde el aviso previo.
 */
export function useSsoDomains() {
  return useQuery<string[]>({
    queryKey: ['recognition', 'sso-domains'],
    queryFn: () => userAdminApi.getSsoDomains(),
    staleTime: 60 * 60 * 1000,
    retry: 0,
  });
}

// ─── RF-008 — Avatar Frontline dinámico ──────────────────────────────────────────

/**
 * GET v1/users/avatars — catálogo DINÁMICO de avatares (RF-008). Anónimo y estable: el backend lista
 * los blobs disponibles con su URL SAS lista para `<img>`. `staleTime` alto (el catálogo casi no
 * cambia); una sola entrada de caché alimenta tanto el picker como la resolución id→url del avatar.
 */
export function useFrontlineAvatars() {
  return useQuery<FrontlineAvatar[]>({
    queryKey: ['recognition', 'avatars'],
    queryFn: () => userAdminApi.getFrontlineAvatars(),
    staleTime: 60 * 60 * 1000, // 1 h — catálogo estable
    retry: 0,
  });
}

/**
 * Resuelve el ID de un avatar (nombre del blob que persiste el perfil) a su URL usando el catálogo
 * cacheado (`useFrontlineAvatars`). Devuelve `undefined` si el id es null/vacío o no está en el
 * catálogo — el consumidor cae entonces a un placeholder (inicial/carita neutra), nunca a una rota.
 */
export function useAvatarUrl(avatarId: string | null | undefined): string | undefined {
  const { data } = useFrontlineAvatars();
  if (!avatarId) return undefined;
  return data?.find((a) => a.id === avatarId)?.url;
}

/**
 * Igual que `useAvatarUrl` pero para **listas**: devuelve una función `id → url`.
 *
 * 🔴 Existe por una restricción de React, no por gusto: `useAvatarUrl` es un hook y no se puede
 * llamar dentro de un `.map()`. Las pantallas que pintan a N personas (selector de receptores,
 * etiquetado, búsqueda de colegas) llaman a ESTE una sola vez arriba y luego resuelven cada
 * persona dentro del bucle. Comparte la misma `queryKey` que `useAvatarUrl`, así que N avatares
 * siguen costando **una** petición del catálogo.
 */
export function useAvatarUrlResolver(): (avatarId: string | null | undefined) => string | undefined {
  const { data } = useFrontlineAvatars();
  return (avatarId) => (avatarId ? data?.find((a) => a.id === avatarId)?.url : undefined);
}

/**
 * GET v1/users/{id}/profile — el perfil del usuario, que es **el único endpoint que devuelve el
 * avatar** (RF-008). No se usa `resolve` porque su DTO (`UserDto`) está congelado y no lo lleva.
 *
 * `enabled` por id: sin id no hay a quién pedirle el perfil.
 */
export function useUserProfile(id: string | null | undefined) {
  return useQuery<UserProfile>({
    queryKey: recognitionKeysExt.userProfile(id ?? ''),
    queryFn: () => userAdminApi.getUserProfile(id as string),
    enabled: Boolean(id),
    retry: 0,
  });
}

/**
 * PATCH v1/users/{id}/avatar — persiste el avatar elegido (id del catálogo dinámico, RF-008).
 *
 * 🔴 Devuelve `UserProfile`, no `{ success: boolean }`. El tipo anterior **mentía**: el backend
 * responde con el perfil completo (`UserProfileDto`), así que cualquiera que hubiera leído
 * `data.success` habría obtenido `undefined`.
 *
 * Y ahora **invalida**: sin esto, tras guardar el avatar no se refrescaba ni el perfil ni el sidebar
 * hasta recargar la página a mano.
 */
export function useUpdateAvatar() {
  const queryClient = useQueryClient();
  return useMutation<UserProfile, unknown, { id: string; body: UpdateAvatarBody }>({
    mutationFn: ({ id, body }) => userAdminApi.updateAvatar(id, body),
    onSuccess: (_data, { id }) => {
      void queryClient.invalidateQueries({ queryKey: recognitionKeysExt.userProfile(id) });
    },
  });
}

// ─── WP-16 REQ-140 — Aceptaciones legales ───────────────────────────────────────

/** GET v1/legal-acceptances/status — documentos legales pendientes de (re)aceptar (bloqueante). */
export function useLegalStatus(enabled = true) {
  return useQuery<LegalAcceptanceStatus>({
    queryKey: recognitionKeysExt.legalStatus(),
    queryFn: () => userAdminApi.getLegalStatus(),
    enabled,
    retry: 0,
  });
}

/**
 * GET v1/legal-documents/{type}?lang=&ouId= — contenido versionado del documento legal (no
 * hardcodeado), que puede venir como PDF del módulo Documentos o como texto por secciones.
 *
 * `ouId` entra en la `queryKey`: sin él, al elegir otra organización se serviría la respuesta
 * cacheada de la anterior y el modal enseñaría el documento de otra geografía.
 */
export function useLegalDocument(type: LegalDocType | null, lang: string, ouId?: string) {
  return useQuery<LegalDocumentDto>({
    queryKey: recognitionKeysExt.legalDoc(type ?? 'privacy', lang, ouId),
    queryFn: () => userAdminApi.getLegalDocument(type as LegalDocType, lang, ouId),
    enabled: Boolean(type),
    retry: 0,
  });
}

/** POST v1/legal-acceptances — registra la aceptación de una versión e invalida el estado. */
export function useAcceptLegal() {
  const qc = useQueryClient();
  const { demoUser } = useUserContext();
  return useMutation<{ success: boolean }, unknown, LegalAcceptanceBody>({
    mutationFn: (body) => userAdminApi.acceptLegal({ ...body, userId: demoUser.userId }),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.legalStatus() }),
  });
}

/** Query key del borrador de un documento legal. */
const legalDraftKey = (type: LegalDocType) => ['recognition', 'legal', 'draft', type] as const;

/** GET v1/legal-documents/{type}/draft — borrador editable (admin). */
export function useLegalDocumentDraft(type: LegalDocType) {
  return useQuery<LegalDocumentDraftDto>({
    queryKey: legalDraftKey(type),
    queryFn: () => userAdminApi.getLegalDocumentDraft(type),
  });
}

/** PUT v1/legal-documents/{type}/draft — guarda el borrador sin disparar re-aceptación. */
export function useSaveLegalDocumentDraft(type: LegalDocType) {
  const qc = useQueryClient();
  return useMutation<LegalDocumentDraftDto, unknown, SaveLegalDocumentDraftBody>({
    mutationFn: (body) => userAdminApi.saveLegalDocumentDraft(type, body),
    onSuccess: (draft) => qc.setQueryData(legalDraftKey(type), draft),
  });
}

/** POST v1/legal-documents/{type}/publish — publica el borrador como versión vigente (bloqueante). */
export function usePublishLegalDocument(type: LegalDocType) {
  const qc = useQueryClient();
  return useMutation<
    { tenantId: string; documentKey: string; version: string; isCurrent: boolean; effectiveAt: string },
    unknown,
    PublishLegalDocumentBody
  >({
    mutationFn: (body) => userAdminApi.publishLegalDocument(type, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: recognitionKeysExt.legalDoc(type, 'es') }),
  });
}

// ─── WP-08 — Reportería de Reconocimiento (AnalyticsService) ─────────────────────
//
// Estados honestos: cada hook expone loading/error/empty reales de TanStack Query. `retry: 0` para
// no martillar endpoints que aún puedan no estar desplegados en D5 y mostrar el error rápido.

/** Claves de query de la reportería (invalidación selectiva). */
export const recognitionReportKeys = {
  daily: (p: RecognitionReportParams, funnel: DailyFunnelFilter = {}) =>
    ['recognition', 'reports', 'daily', p, funnel] as const,
  campaign: (p: RecognitionReportParams) => ['recognition', 'reports', 'campaign', p] as const,
  // Solo `ou`: el bloque de plantilla no depende del rango de fechas, así que cambiar el calendario
  // NO debe invalidar esta consulta ni volver a pedirla.
  headcount: (ou?: string) => ['recognition', 'reports', 'headcount', ou ?? ''] as const,
  history: (p: RecognitionReportParams) => ['recognition', 'reports', 'history', p] as const,
  campaignLive: (p: RecognitionReportParams) => ['recognition', 'reports', 'campaign-live', p] as const,
  /**
   * Punto 13 (revisión final, 2026-09-03) — recortada a lo que el endpoint REALMENTE liga
   * (`scope`/`scopeIds`/`from`/`to`, ver `toReportRangeQuery`): `fundamentalId`/`personaId` viajan
   * en la query pero el handler del Heatmap no los usa (por eso `HeatmapTab` recorta por Fundamental
   * EN CLIENTE, ver `celdasFiltradasPorFundamental`). Con `p` completo como clave, cada cambio de
   * esos dos filtros disparaba un refetch que devolvía exactamente lo mismo.
   */
  heatmap: (p: RecognitionReportParams) =>
    ['recognition', 'reports', 'heatmap', p.ou ?? '', p.ouIds?.join(',') ?? '', p.startDate ?? '', p.endDate ?? ''] as const,
  network: (p: RecognitionReportParams) => ['recognition', 'reports', 'network', p] as const,
  generational: (p: RecognitionReportParams, filters: GenerationalFilters) =>
    ['recognition', 'reports', 'generational', p, filters] as const,
  generationalDistribution: (p: RecognitionReportParams) =>
    ['recognition', 'reports', 'generationalDistribution', p] as const,
  leaders: (p: RecognitionReportParams) => ['recognition', 'reports', 'leaders', p] as const,
  leadersList: (p: RecognitionReportParams) => ['recognition', 'reports', 'leaders-list', p] as const,
  /**
   * 2026-09-04 — `fundamentalId` vuelve a la clave (vuelta atrás del punto 5 de la ola anterior,
   * ver `client.ts`). Sin él, elegir un Fundamental distinto en la tira serviría de caché el resumen
   * del Fundamental ANTERIOR bajo el mismo `campaignId`: react-query no distinguiría las dos
   * peticiones y los cinco KPIs de `ShineTab.tsx` quedarían leyendo el número equivocado sin ningún
   * síntoma visible — ni error, ni loading, ni hueco. `|| ''` normaliza vacío/`undefined` al mismo
   * segmento, igual que el resto de las claves de este objeto.
   */
  // D1 — resumen con el recorte del reporte: la clave lleva los mismos parámetros que `campaign`.
  campaignSummaryDelReporte: (p: RecognitionReportParams) =>
    ['recognition', 'reports', 'campaign-summary-reporte', p] as const,
  campaignPoints: (campaignId: string, from?: string, to?: string, ouIds?: readonly string[]) =>
    ['recognition', 'reports', 'campaign-points', campaignId, from ?? '', to ?? '', ouIds?.join(',') ?? ''] as const,
  campaignSummary: (campaignId: string, fundamentalId?: string, site?: string) =>
    ['recognition', 'reports', 'campaign-summary', campaignId, fundamentalId || '', site || ''] as const,
};

/**
 * GET v1/analytics/recognition/daily — Tab Diario (REQ-077).
 * E5 — `funnel` (anio/mes) es el filtro propio del Embudo de Adopción, independiente de `params`.
 */
export function useRecognitionDaily(params: RecognitionReportParams, funnel: DailyFunnelFilter = {}) {
  return useQuery<DailyReportDto>({
    queryKey: recognitionReportKeys.daily(params, funnel),
    queryFn: () => recognitionReportsApi.getDaily(params, funnel),
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/headcount — bloque de plantilla del armazón de Reportería.
 *
 * `staleTime` alto a propósito: el censo se refresca UNA vez al día (worker de las 05:00 UTC), así que
 * volver a pedirlo al cambiar de pestaña sería tráfico sin ninguna posibilidad de traer algo nuevo.
 */
export function useRecognitionHeadcount(params: { ou?: string; ouIds?: readonly string[] } = {}) {
  return useQuery<HeadcountSummaryDto>({
    queryKey: recognitionReportKeys.headcount(params.ouIds?.join(',') || params.ou),
    queryFn: () => recognitionReportsApi.getHeadcount({ ou: params.ou, ouIds: params.ouIds }),
    staleTime: 15 * 60 * 1000,
    retry: 0,
  });
}

/** GET v1/analytics/recognition/campaign — Tab Brillamos: ranking de campaña (REQ-078). */
export function useRecognitionCampaign(params: RecognitionReportParams, enabled = true) {
  return useQuery<CampaignReportDto>({
    queryKey: recognitionReportKeys.campaign(params),
    queryFn: () => recognitionReportsApi.getCampaign(params),
    enabled,
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/history — RF-150: histórico completo de una campaña. `enabled` se
 * ata a tener un campaignId elegido, igual que useRecognitionCampaignLive — no tiene sentido
 * llamar sin campaña.
 */
export function useRecognitionHistory(params: RecognitionReportParams, enabled = true) {
  return useQuery<HistoryReportDto>({
    queryKey: recognitionReportKeys.history(params),
    queryFn: () => recognitionReportsApi.getHistory(params),
    enabled,
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/campaign-summary — RF-151: Reconocimientos/Audiencia de cada
 * campaña visible en el tab "Historial de campañas". Una query por campaña (react-query cachea
 * cada campaignId por separado); `useQueries` mantiene el orden de `campaignIds` en el resultado.
 *
 * `fundamentalId` (2026-09-04) es opcional: `CampaignsGeneralTab` e `HistoryTab` no muestran la
 * barra de filtros y siguen llamando sin él (resumen completo de la campaña, sin acotar). Solo
 * `ShineTab` —que SÍ muestra la tira— lo pasa. Mismo criterio para `site` (2026-09-24).
 */
export function useCampaignSummaries(campaignIds: string[], fundamentalId?: string, site?: string) {
  return useQueries({
    queries: campaignIds.map((campaignId) => ({
      queryKey: recognitionReportKeys.campaignSummary(campaignId, fundamentalId, site),
      queryFn: () => recognitionReportsApi.getCampaignSummary(campaignId, undefined, fundamentalId, site),
      enabled: Boolean(campaignId),
      retry: 0,
      staleTime: 5 * 60 * 1000,
    })),
  });
}

/**
 * D1 (2026-09-30) — tarjetas de Brillamos Juntos con el MISMO recorte que su ranking
 * (`useRecognitionCampaign`). Recibe los mismos `params`, así que cualquier filtro que mueva el
 * ranking mueve también las tarjetas.
 */
export function useCampaignSummaryDelReporte(params: RecognitionReportParams, enabled = true) {
  return useQuery<CampaignSummaryDto>({
    queryKey: recognitionReportKeys.campaignSummaryDelReporte(params),
    queryFn: () => recognitionReportsApi.getCampaignSummaryDelReporte(params),
    // BJT-1 (2026-10-14) — una campaña o la lista de «Todas las campañas».
    enabled: enabled && Boolean(params.campaignId || params.campaignIds?.length),
    retry: 0,
  });
}

/**
 * D4 (2026-09-30) — puntos de una campaña. `enabled` lo decide quien llama: solo administradores de
 * puntos y solo campañas con «Aplican puntos»; fuera de eso no se pide nada.
 */
export function useCampaignPoints(
  campaignId: string,
  filtros: { from?: string; to?: string; ouIds?: readonly string[]; campaignIds?: readonly string[] },
  enabled: boolean,
) {
  // BJT-1 (2026-10-14) — con «Todas las campañas» la clave lleva la lista en lugar del id.
  const clave = campaignId || (filtros.campaignIds ?? []).join(',');
  return useQuery<CampaignPointsDto>({
    queryKey: recognitionReportKeys.campaignPoints(clave, filtros.from, filtros.to, filtros.ouIds),
    queryFn: () => rulesApi.getCampaignPoints(campaignId, filtros),
    enabled: enabled && Boolean(clave),
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/campaign-live — KPIs en vivo de una campaña (REQ-082).
 * `enabled` se ata a tener un campaignId resuelto para no llamar sin campaña.
 */
export function useRecognitionCampaignLive(params: RecognitionReportParams, enabled = true) {
  return useQuery<CampaignLiveKpiDto>({
    queryKey: recognitionReportKeys.campaignLive(params),
    queryFn: () => recognitionReportsApi.getCampaignLive(params),
    enabled,
    retry: 0,
    refetchInterval: enabled ? 60 * 1000 : false,
  });
}

/**
 * GET v1/analytics/recognition/heatmap — Tab Heatmap: celdas OU × Fundamental (REQ-081).
 *
 * C7 (2026-08-13) — `enabled` (default `true`, compatible con todos los llamadores existentes) deja
 * que quien SABE que el actor no tiene la capacidad `MapaDeCalor` (solo AdminGlobal, ver
 * `CapabilityPolicy`) evite disparar la petición: el backend igual devuelve 403 para el resto, pero
 * llamarlo a sabiendas de que va a fallar es ruido — el caso real es el export consolidado (C7),
 * que también necesita las celdas del Heatmap y se monta para los tres roles admin del panel.
 */
export function useRecognitionHeatmap(params: RecognitionReportParams, enabled = true) {
  return useQuery<HeatmapCellDto[]>({
    queryKey: recognitionReportKeys.heatmap(params),
    queryFn: () => recognitionReportsApi.getHeatmap(params),
    enabled,
    retry: 0,
  });
}

/** GET v1/analytics/recognition/network — Tab Red ONA: grafo cross-OU (REQ-081). */
export function useRecognitionNetwork(params: RecognitionReportParams) {
  return useQuery<OnaGraphDto>({
    queryKey: recognitionReportKeys.network(params),
    queryFn: () => recognitionReportsApi.getNetwork(params),
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/generational — Tab Demográfico: distribución por banda generacional.
 * RF-152 — `filters` (BU/Depto/Site/Líderes-Pares) son propios del tab, independientes de `params`.
 *
 * C5/C7 (2026-08-13, BREAKING) — el shape de respuesta pasó de `RankedEntryDto[]` a
 * `GenerationalReportDto` (`{ bandas, ousConCobertura }`). Ver el DTO para el detalle.
 */
export function useRecognitionGenerational(
  params: RecognitionReportParams,
  filters: GenerationalFilters = {},
) {
  return useQuery<GenerationalReportDto>({
    queryKey: recognitionReportKeys.generational(params, filters),
    queryFn: () => recognitionReportsApi.getGenerational(params, filters),
    retry: 0,
  });
}

/** GET v1/analytics/recognition/generational-distribution — Censo de plantilla por banda generacional. */
export function useRecognitionGenerationalDistribution(params: RecognitionReportParams) {
  return useQuery<GenerationalDistributionReportDto>({
    queryKey: recognitionReportKeys.generationalDistribution(params),
    queryFn: () => recognitionReportsApi.getGenerationalDistribution(params),
    retry: 0,
  });
}

/** GET v1/analytics/recognition/leaders — Tab Líderes: métricas por OU para OU Ambassadors (REQ-088). */
export function useRecognitionLeaders(params: RecognitionReportParams) {
  return useQuery<LeaderMetricRowDto[]>({
    queryKey: recognitionReportKeys.leaders(params),
    queryFn: () => recognitionReportsApi.getLeaders(params),
    retry: 0,
  });
}

/** GET v1/analytics/recognition/leaders-list — Fase F: listado agregado de líderes (persona, no OU). */
export function useRecognitionLeadersList(params: RecognitionReportParams) {
  return useQuery<LeadersListResponseDto>({
    queryKey: recognitionReportKeys.leadersList(params),
    queryFn: () => recognitionReportsApi.getLeadersList(params),
    retry: 0,
  });
}

/**
 * GET v1/analytics/recognition/winners-export?format= — export de ganadores por cascadeo (WP-08).
 * Lazy (`enabled: false`): la UI dispara `refetch()` al pulsar "Descargar" y genera el Excel con la
 * data devuelta. `gcTime: 0` para no cachear un export puntual.
 */
export function useWinnersExport(format: WinnersExportFormat, params: RecognitionReportParams) {
  return useQuery<WinnerDto[]>({
    queryKey: ['recognition', 'reports', 'winners-export', format, params] as const,
    queryFn: () => recognitionReportsApi.getWinnersExport(format, params),
    enabled: false,
    retry: 0,
    gcTime: 0,
  });
}

// ─── REQ-124 — Vista de equipo (líder) ───────────────────────────────────────────

/** GET v1/analytics/recognition/team — métricas del equipo del líder. retry 0 (estado honesto). */
export function useRecognitionTeam(params: RecognitionTeamParams = {}) {
  return useQuery<TeamMetricsDto>({
    queryKey: recognitionKeysExt.team(params),
    queryFn: () => recognitionReportsApi.getTeam(params),
    retry: 0,
  });
}

// ─── REQ-125 — Tablero ejecutivo K-01..K-12 ──────────────────────────────────────

/** GET v1/analytics/recognition/summary — KPIs del tablero (K-01..K-12). retry 0 (estado honesto). */
export function useRecognitionSummary(params: RecognitionSummaryParams = {}) {
  return useQuery<RecognitionKpiDto[]>({
    queryKey: recognitionKeysExt.summary(params),
    queryFn: () => recognitionReportsApi.getSummary(params),
    retry: 0,
  });
}

// ─── WP-15 REQ-129..134 — Admin (catálogo, moderación, ganadores, campañas, gestión) ───
//
// Claves de query namespaced para invalidación selectiva de cada sección del admin.
export const adminKeys = {
  pills: () => ['recognition', 'pills'] as const,
  pillsForMe: () => ['recognition', 'pills', 'for-me'] as const,
  reports: (status: string | undefined) => ['recognition', 'moderation', 'reports', status ?? 'all'] as const,
  reportDetail: (id: string) => ['recognition', 'moderation', 'report', id] as const,
  // Los filtros de la tira entran en la clave: sin ellos, cambiar de OU o de rango serviría la
  // respuesta cacheada del filtro anterior. El prefijo no cambia, así que las invalidaciones por
  // rama (`recognitionKeys.all`) siguen alcanzando todas las variantes.
  moderationReport: (params: RecognitionReportParams = {}) =>
    ['recognition', 'moderation', 'report-aggregate', params] as const,
  blockedWords: (country: string | undefined, language: string | undefined) =>
    ['recognition', 'moderation', 'blocked-words', country ?? '*', language ?? '*'] as const,
  moderationSettings: (ouId: string | undefined) => ['recognition', 'moderation', 'settings', ouId ?? 'global'] as const,
  countryLanguages: () => ['recognition', 'moderation', 'country-languages'] as const,
  /** RF-090 — destinatarios del ✉️ de un caso concreto. */
  notifyAdminPreview: (reportId: string) =>
    ['recognition', 'moderation', 'notify-admin-preview', reportId] as const,
  winners: (campaignId: string | undefined, ouId: string | undefined) =>
    ['recognition', 'winners', campaignId ?? 'all', ouId ?? 'all'] as const,
  campaigns: (status: CampaignStatus | undefined, page: number, pageSize: number) =>
    ['recognition', 'campaigns', 'list', status ?? 'all', page, pageSize] as const,
  // Cuelga de 'campaigns' a propósito: toda mutación de campaña que invalida ese prefijo lo refresca.
  campaignsByEstadoVisible: (estadoVisible: CampaignEstadoVisible | undefined, page: number, pageSize: number) =>
    ['recognition', 'campaigns', 'list-visible', estadoVisible ?? 'none', page, pageSize] as const,
  campaign: (id: string) => ['recognition', 'campaigns', 'detail', id] as const,
  // Cuelga de 'campaigns': cualquier mutación de campaña lo invalida. Las de excepciones lo invalidan
  // aparte (ver `invalidateSchedulesOverview`).
  campaignSchedulesOverview: () => ['recognition', 'campaigns', 'schedules-overview'] as const,
  pendingApprovals: () => ['recognition', 'campaigns', 'approvals', 'pending'] as const,
  myApprovals: () => ['recognition', 'campaigns', 'approvals', 'mine'] as const,
  // CPE-138 — Campañas por Localidad (excepciones).
  campaignExceptions: (params: CampaignExceptionListParams) =>
    ['recognition', 'campaign-exceptions', 'list', params] as const,
  campaignExceptionApprovals: () => ['recognition', 'campaign-exceptions', 'approvals', 'pending'] as const,
  // Clave POR PROGRAMA y sin params de listado: la resuelve el servidor con la identidad del
  // llamador, así que no hay filtros del cliente que formen parte de la identidad de la consulta.
  activeCampaignException: (programa: string) =>
    ['recognition', 'campaign-exceptions', 'active-for-me', programa] as const,
  gestion: () => ['recognition', 'gestion', 'settings'] as const,
  recapEditions: () => ['recognition', 'recap', 'editions'] as const,
  recapEdition: (id: string) => ['recognition', 'recap', 'edition', id] as const,
  /**
   * RF-130 (criterio 1) — edición vigente para el usuario final (no admin), por OU y año. El año es
   * OBLIGATORIO en la key: sin él, TanStack Query serviría de caché la edición de otro año (p. ej. al
   * cambiar de año dentro de la misma sesión sin recargar).
   */
  recapCurrent: (ouId: string, anio?: number) =>
    ['recognition', 'recap', 'current', ouId, anio ?? ''] as const,
  /** RF-130 — disponibilidad del Wrapped del titular. Sin parámetros: sale del token. */
  wrappedAvailability: () => ['recognition', 'recap', 'availability'] as const,
  badges: () => ['recognition', 'gestion', 'badges'] as const,
};

// ─── FASE 4 — Documentation Pills del rail (CPE-162/163/164) ─────────────────────

/** GET v1/documentation-pills — listado de administración de pills (activas e inactivas). */
export function useDocumentationPills(enabled = true) {
  return useQuery<DocumentationPillDto[]>({
    queryKey: adminKeys.pills(),
    queryFn: () => catalogApi.getPills(),
    enabled,
    // 1 reintento: recupera un fallo TRANSITORIO de primera carga (red / token aún refrescando) que
    // antes dejaba la tabla vacía hasta que otra acción refetcheaba. Sigue siendo barato ante un 403
    // real (2 llamadas, no 4). El estado de error además ofrece un botón "Reintentar" manual.
    retry: 1,
  });
}

/**
 * Predicado de reintento del rail — equivalente al del cliente de catálogo (`wt`/`r0` en el bundle):
 * reintenta hasta 2 veces ante un fallo TRANSITORIO:
 *   - `NO_AUTH_TOKEN_STATUS`: la adquisición de token aún no dio resultado (hueco de renovación
 *     MSAL, SSO de Teams no listo tras suspensión). La petición ni salió — reintentar a 1s/2s la
 *     recupera SIN emitir un 401 al servidor.
 *   - `5xx`: servicio momentáneamente inalcanzable.
 *
 * NO se reintenta ante un `401` del servidor, deliberadamente. Reintentar solo ayuda si el 401 es
 * un token recién caducado que se está renovando por detrás; si resulta ser un token que el
 * servidor rechaza por otro motivo (audiencia, firma, desfase de reloj), reintentar no recupera
 * nada y triplica los 401. Cuál de los dos es está sin resolver: lo decide la Consulta 26 sobre
 * `ai-postpulse-prd` una vez desplegada la instrumentación de `OnChallenge` (PR #122 de
 * SurveyService). Hasta entonces no se añade el reintento.
 * Ante un 403 real u otro 4xx no se reintenta y la landing sigue sin romperse (el rail degrada en
 * silencio).
 */
function shouldRetryRail(failureCount: number, error: unknown): boolean {
  const status = getRecognitionErrorStatus(error);
  const recuperable =
    status === NO_AUTH_TOKEN_STATUS || (typeof status === 'number' && status >= 500);
  return recuperable && failureCount < 2;
}

/** Backoff exponencial acotado: 1s, 2s, 4s… con techo de 15s. */
const railRetryDelay = (attempt: number): number => Math.min(1000 * 2 ** attempt, 15_000);

/** GET v1/documentation-pills/for-me — pills activas visibles para el usuario (alimenta el rail). */
export function usePillsForMe(enabled = true) {
  return useQuery<DocumentationPillDto[]>({
    queryKey: adminKeys.pillsForMe(),
    queryFn: () => catalogApi.getPillsForMe(),
    enabled,
    // El rail sondea cada 60s: un fallo transitorio NO debe dejarlo caído hasta el siguiente tick.
    // Se reintenta ante token-no-disponible y 5xx (hasta 2, con backoff acotado); ante un 401 o
    // cualquier otro 4xx no se reintenta y la landing sigue sin romperse (el rail degrada en
    // silencio). El reintento ante 401 queda pendiente de la Consulta 26 — ver shouldRetryRail.
    retry: shouldRetryRail,
    retryDelay: railRetryDelay,
    // Bug fila 4 (Status Reconocimiento) — HomeRightRail vive en AppLayout y NUNCA se desmonta al
    // navegar dentro de la SPA (mismo caso ya resuelto para el badge de notificaciones, ver
    // useNotificationsInbox): `invalidateQueries` en la mutación de admin solo alcanza al QueryClient
    // de SU PROPIA pestaña/sesión. Un admin desactivando una pill en su sesión no puede invalidar la
    // caché de la sesión YA ABIERTA de otro usuario — con el `staleTime` global de 5 min y sin
    // refetch periódico, esa sesión servía la pill inactiva desde caché indefinidamente (hasta un
    // refresh de página o 5 min de foco de ventana). El sondeo la mantiene razonablemente al día.
    //
    // El sondeo de 60s se mantiene (mismo motivo del bug fila 4), pero SOLO con la pestaña visible.
    // `refetchIntervalInBackground` pasa a `false`: sondear pestañas ocultas multiplicaba x5 la
    // exposición a un token inválido (telemetría PROD: `for-me` era el mayor emisor de 401 del
    // landing) sin beneficio real — al volver a la pestaña, `refetchOnWindowFocus` ya refresca el
    // dato porque a los 60s ya está "stale" (ver `staleTime`). El caso "admin desactiva una pill en
    // otra pestaña" se cubre igual: el usuario ve el cambio en cuanto vuelve al rail.
    refetchInterval: enabled ? 60 * 1000 : false,
    refetchIntervalInBackground: false,
    // `staleTime` sube de 0 a 60s (dato poco cambiante: un catálogo de píldoras). Evita el refetch
    // redundante en cada foco de ventana dentro del primer minuto. Sigue siendo la red de seguridad
    // para el navegador REAL que congela los timers de una pestaña en segundo plano: cualquier
    // regreso tras >60s (portátil que despierta, cambio de pestaña largo) encuentra el dato "stale"
    // y `refetchOnWindowFocus` (activo por omisión) dispara el refetch sin depender del sondeo.
    staleTime: 60 * 1000,
  });
}

/** POST v1/documentation-pills — alta de una pill. Invalida el listado admin y el rail. */
export function useCreatePill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: UpsertPillBody) => catalogApi.createPill(body),
    // adminKeys.pillsForMe() es descendiente de adminKeys.pills() (comparten prefijo de queryKey),
    // así que invalidar esta última ya alcanza al rail sin necesidad de una segunda invalidación.
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.pills() }),
  });
}

/** PUT v1/documentation-pills/{id} — edición de una pill. Invalida el listado admin y el rail. */
export function useUpdatePill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpsertPillBody }) => catalogApi.updatePill(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.pills() }),
  });
}

/** DELETE v1/documentation-pills/{id} — baja de una pill. Invalida el listado admin y el rail. */
export function useDeletePill() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => catalogApi.deletePill(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.pills() }),
  });
}

// ─── REQ-130 — Moderación ────────────────────────────────────────────────────────

/** GET v1/moderation/reports?status= — bandeja de reportes. */
export function useModerationReports(status?: string) {
  return useQuery<ReportDto[]>({
    queryKey: adminKeys.reports(status),
    queryFn: () => moderationApi.getReports(status),
    retry: 0,
  });
}

/**
 * GET v1/moderation/report — Reportería de Moderación (WP-08, Fase 3): KPIs + detalle de casos.
 *
 * `params` son los filtros de la tira de Reportería; el endpoint honra OU, rango y persona, y NO
 * Fundamental (ver `toModerationReportQuery`). Sin argumentos devuelve todo lo alcanzable, que es lo
 * que necesita cualquier consumidor fuera de Reportería.
 */
export function useModerationReport(params: RecognitionReportParams = {}) {
  return useQuery<ModerationReportDto>({
    queryKey: adminKeys.moderationReport(params),
    queryFn: () => moderationApi.getReport(params),
    retry: 0,
  });
}

/** GET v1/moderation/reports/{id} — detalle del caso (canalización). */
export function useModerationReportDetail(id: string | undefined) {
  return useQuery<ReportDetailDto>({
    queryKey: adminKeys.reportDetail(id ?? ''),
    queryFn: () => moderationApi.getReportDetail(id as string),
    enabled: Boolean(id),
    retry: 0,
  });
}

/**
 * RF-084 — las acciones de moderación cambian la VISIBILIDAD del reconocimiento, así que invalidan
 * `recognitionKeys.all` (`['recognition']`) y no solo la bandeja: el muro vive bajo
 * `['recognition','feed', …]`, que es rama HERMANA de `['recognition','moderation']` y por tanto no
 * la alcanza un `invalidateQueries` con ese prefijo. Antes el muro seguía pintando la caché anterior
 * hasta un recargado duro. Mismo criterio que `useReportRecognition`.
 */
const invalidateRecognitionTree = (qc: ReturnType<typeof useQueryClient>) => () =>
  qc.invalidateQueries({ queryKey: recognitionKeys.all });

/** POST v1/moderation/{id}/hide — confirma el reporte e invalida bandeja y muro. */
export function useHideRecognition() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => moderationApi.hide(id),
    onSuccess: invalidateRecognitionTree(qc),
  });
}

/** POST v1/moderation/{id}/restore — desestima el reporte e invalida bandeja y muro. */
export function useRestoreRecognition() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => moderationApi.restore(id),
    onSuccess: invalidateRecognitionTree(qc),
  });
}

/** POST v1/moderation/{id}/reopen — devuelve un caso restaurado a Pendiente e invalida bandeja y muro. */
export function useReopenReport() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => moderationApi.reopen(id),
    onSuccess: invalidateRecognitionTree(qc),
  });
}

/** GET v1/moderation/blocked-words — catálogo de palabras bloqueadas. */
export function useBlockedWords(country?: string, language?: string) {
  return useQuery<BlockedWordDto[]>({
    queryKey: adminKeys.blockedWords(country, language),
    queryFn: () => moderationApi.getBlockedWords(country, language),
    retry: 0,
  });
}

/** POST v1/moderation/blocked-words — alta de palabra bloqueada. */
export function useCreateBlockedWord() {
  const qc = useQueryClient();
  return useMutation<BlockedWordDto, unknown, BlockedWordBody>({
    mutationFn: (body) => moderationApi.createBlockedWord(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'moderation', 'blocked-words'] }),
  });
}

/** PUT v1/moderation/blocked-words/{id} — edición en línea (lápiz → check) del maquetado. */
export function useUpdateBlockedWord() {
  const qc = useQueryClient();
  return useMutation<BlockedWordDto, unknown, { id: string; body: BlockedWordBody }>({
    mutationFn: ({ id, body }) => moderationApi.updateBlockedWord(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'moderation', 'blocked-words'] }),
  });
}

/** DELETE v1/moderation/blocked-words/{id} — baja de palabra bloqueada. */
export function useDeleteBlockedWord() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => moderationApi.deleteBlockedWord(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'moderation', 'blocked-words'] }),
  });
}

/**
 * GET v1/moderation/reports/{id}/notify-admin/preview — RF-090, a quién llegaría el ✉️.
 *
 * Se pide al abrir el modal de confirmación (`reportId` undefined = deshabilitada). `retry: 0` y
 * `staleTime: 0`: es una foto del directorio en el instante de decidir, no un catálogo; reintentar
 * en silencio solo alargaría la espera del moderador ante un directorio caído, que es justamente un
 * resultado que tiene que VER.
 */
export function useModerationNotifyPreview(reportId: string | undefined) {
  return useQuery<ModerationNotifyPreviewDto>({
    queryKey: adminKeys.notifyAdminPreview(reportId ?? ''),
    queryFn: () => moderationApi.notifyAdminPreview(reportId!),
    enabled: Boolean(reportId),
    retry: 0,
    staleTime: 0,
    gcTime: 0,
  });
}

/**
 * POST v1/moderation/reports/{id}/notify-admin — RF-090, botón ✉️ de la bandeja.
 *
 * Escala el caso al admin de la OU/país sin esperar al digest semanal. NO invalida la bandeja: el
 * aviso no cambia el estado del reporte, y refrescarla reordenaría la cola FIFO sin motivo (RF-089).
 */
export function useNotifyModerationAdmin() {
  return useMutation<{ success: boolean; recipients: number }, unknown, string>({
    mutationFn: (reportId) => moderationApi.notifyAdmin(reportId),
  });
}

/**
 * GET v1/moderation/country-languages — tabla país → idiomas hablados (RF-082).
 *
 * Alimenta los selectores del gestor de palabras: la taxonomía la manda el servidor, no un arreglo
 * hardcodeado en la pantalla, que era justo lo que mantenía vivos los códigos no-ISO del maquetado.
 */
export function useCountryLanguages() {
  return useQuery<CountryLanguageMapDto>({
    queryKey: adminKeys.countryLanguages(),
    queryFn: () => moderationApi.getCountryLanguages(),
    retry: 0,
  });
}

/** PUT v1/moderation/country-languages — edita la tabla (solo alcance global). */
export function useSaveCountryLanguages() {
  const qc = useQueryClient();
  return useMutation<CountryLanguageMapDto, unknown, CountryLanguageMapDto>({
    mutationFn: (body) => moderationApi.saveCountryLanguages(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: adminKeys.countryLanguages() });
      // El catálogo de palabras se muestra agrupado por la taxonomía de la tabla: si cambia, la
      // agrupación de la pantalla queda obsoleta aunque las palabras no se hayan tocado.
      qc.invalidateQueries({ queryKey: ['recognition', 'moderation', 'blocked-words'] });
    },
  });
}

/** GET v1/view-settings/moderation — toggles de moderación. */
export function useModerationSettings(ouId?: string) {
  return useQuery<ModerationSettingsDto>({
    queryKey: adminKeys.moderationSettings(ouId),
    queryFn: () => moderationApi.getSettings(ouId),
    retry: 0,
  });
}

/**
 * WP-F3 — vistas EFECTIVAS de la OU del usuario. Lo consumen las pantallas del colaborador.
 *
 * `staleTime` alto a propósito: es configuración de administración, no un dato vivo, y cada pantalla
 * que la consulte compartiría la misma entrada de caché.
 */
export function useUserViews(ouId?: string) {
  return useQuery<UserViewSettingsDto>({
    queryKey: ['recognition', 'user-views', ouId ?? null] as const,
    queryFn: () => userViewsApi.get(ouId),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

/**
 * 2026-11-03 — vistas EFECTIVAS de varias OUs a la vez (misma caché que `useUserViews`). `undefined`
 * en la lista pide el documento global. Lo usa Admin › Resumen para avisar si la vista «Resumen» está
 * apagada para alguna OU del alcance.
 */
export function useUserViewsForOus(ouIds: ReadonlyArray<string | undefined>) {
  return useQueries({
    queries: ouIds.map((ouId) => ({
      queryKey: ['recognition', 'user-views', ouId ?? null] as const,
      queryFn: () => userViewsApi.get(ouId),
      staleTime: 5 * 60 * 1000,
      retry: 0,
    })),
  });
}

/** WP-F3 — documento PROPIO de un nivel, para el admin (distingue «puesto aquí» de «heredado»). */
export function useOwnUserViews(ouId?: string, enabled = true) {
  return useQuery<UserViewSettingsDto>({
    queryKey: ['recognition', 'user-views', 'own', ouId ?? null] as const,
    queryFn: () => userViewsApi.get(ouId, true),
    enabled,
    retry: 0,
  });
}

/** WP-F3 — guarda las vistas de un nivel. */
export function useSaveUserViews() {
  const qc = useQueryClient();
  return useMutation<UserViewSettingsDto, unknown, { body: UserViewSettingsDto; ouId?: string }>({
    mutationFn: ({ body, ouId }) => userViewsApi.save(body, ouId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'user-views'] }),
  });
}

/** PUT v1/view-settings/moderation — guarda los toggles. */
export function useSaveModerationSettings() {
  const qc = useQueryClient();
  return useMutation<ModerationSettingsDto, unknown, ModerationSettingsDto>({
    mutationFn: (body) => moderationApi.saveSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'moderation', 'settings'] }),
  });
}

/** GET v1/view-settings/recognition-rules — reglas efectivas de envío/perfil. `campaignId` resuelve
 *  el permiso de anónimo para esa campaña en particular (ver ReconocerPanel/WordBuilderPanel).
 *  `programa` fuerza de qué programa se resuelven las reglas (Brillamos lo manda explícito para leer
 *  su propio doc aunque no haya campaña activa); sin él se deriva de la campaña o cae a Fundamentales.
 *  `enabled` permite pedirlas solo cuando hacen falta (p. ej. las de la campaña destino de «Enviar también
 *  a Brillamos», con la casilla marcada). */
export function useRecognitionRules(campaignId?: string | null, programa?: string | null, enabled = true) {
  return useQuery<RecognitionRulesDto>({
    queryKey: ['recognition-rules', campaignId ?? null, programa ?? null],
    queryFn: () => gestionApi.getRecognitionRules(campaignId, programa),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
}

// ─── REQ-131 — Ganadores ──────────────────────────────────────────────────────────

/** GET v1/winners — lista de ganadores por campaña/OU. */
export function useWinners(campaignId?: string, ouId?: string, enabled = true) {
  return useQuery<WinnerDto[]>({
    queryKey: adminKeys.winners(campaignId, ouId),
    queryFn: () => winnersApi.getWinners(campaignId, ouId),
    enabled,
    retry: 0,
  });
}

/**
 * RF-157 / WP-D1 — GET v1/winners/search: el listado del admin, filtrado y paginado en SERVIDOR.
 *
 * `placeholderData` mantiene la página anterior mientras llega la siguiente: sin ella, cada
 * pulsación en el buscador vacía la tabla y la pantalla parpadea entre "hay datos" y "no hay nada".
 */
export function useWinnersSearch(params: WinnerSearchParams) {
  return useQuery<WinnersPageDto>({
    queryKey: ['recognition', 'winners', 'search', params],
    queryFn: () => winnersApi.search(params),
    placeholderData: (anterior) => anterior,
    retry: 0,
  });
}

/** PUT v1/winners/{id} — edita un ganador (403 si la ventana está cerrada). */
export function useUpdateWinner() {
  const qc = useQueryClient();
  return useMutation<WinnerDto, unknown, { id: string; body: RegisterWinnerBody }>({
    mutationFn: ({ id, body }) => winnersApi.update(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'winners'] }),
  });
}

/** POST v1/winners/{id}/confirm — Pendiente → Confirmado (CPE-134). */
export function useConfirmWinner() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean; cambio: boolean }, unknown, string>({
    mutationFn: (id) => winnersApi.confirm(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'winners'] }),
  });
}

/** POST v1/winners — registra/actualiza un ganador. */
export function useRegisterWinner() {
  const qc = useQueryClient();
  return useMutation<WinnerDto, unknown, RegisterWinnerBody>({
    mutationFn: (body) => winnersApi.register(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'winners'] }),
  });
}

/**
 * POST v1/winners/import — sube el padrón de Ganadores (D-10), con feedback por fila.
 *
 * Recibe el ARCHIVO y la campaña, no una cadena: el backend lee `.xlsx` (un ZIP binario) y CSV, y
 * convertir el fichero a texto antes de mandarlo destruye el primero.
 *
 * `modo` (Fase 3) es opcional: sin él, `winnersApi.importPadron` sigue mandando "vigente" como
 * hacía antes de la carga histórica.
 */
export function useImportWinners() {
  const qc = useQueryClient();
  return useMutation<WinnerImportResultDto, unknown, ImportWinnersBody>({
    mutationFn: (body) => winnersApi.importPadron(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'winners'] }),
  });
}

/** DELETE v1/winners/{id} — elimina un ganador. */
export function useDeleteWinner() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => winnersApi.remove(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'winners'] }),
  });
}

// ─── REQ-132 — Campañas ─────────────────────────────────────────────────────────

/**
 * GET v1/campaigns — listado paginado por estado.
 * `enabled` permite no disparar el endpoint (de administración) cuando la vista que lo consume
 * está gateada por rol: sin esto un Employee que teclee la URL provoca un 403 innecesario.
 */
/**
 * ADR 0014 — listado de la pestaña Brillamos filtrado por estado visible en el SERVIDOR (no en el
 * cliente: con la lista paginada, «Finalizada» no traería las vencidas que siguen guardadas Activa).
 */
export function useBrillamosCampaignsByEstadoVisible(
  estadoVisible: CampaignEstadoVisible | undefined, page = 1, pageSize = 20, enabled = true,
) {
  return useQuery<PagedResultDto<CampaignDto>>({
    queryKey: adminKeys.campaignsByEstadoVisible(estadoVisible, page, pageSize),
    // `enabled` exige el valor: sin él la consulta no corre, así que el `!` no llega a evaluarse vacío.
    queryFn: () => campaignsApi.getBrillamosCampaignsByEstadoVisible(estadoVisible!, page, pageSize),
    retry: 0,
    enabled: enabled && estadoVisible !== undefined,
  });
}

export function useCampaigns(status: CampaignStatus | undefined, page = 1, pageSize = 20, enabled = true) {
  return useQuery<PagedResultDto<CampaignDto>>({
    queryKey: adminKeys.campaigns(status, page, pageSize),
    queryFn: () => campaignsApi.getCampaigns(status, page, pageSize),
    retry: 0,
    enabled,
  });
}

/**
 * GET v1/campaigns/schedules-overview — horarios y fechas por país de Brillamos de TODAS las OUs.
 * Alimenta la tabla INFORMATIVA «Horarios y Fechas por País»: el Admin OU debe ver los lapsos de todas
 * las OUs (no solo su cascada) para saber cuándo se puede reconocer cross-OU, y por eso no pasa por
 * `useCampaigns`/`useCampaignExceptions`, que el backend acota a su alcance.
 */
export function useCampaignSchedulesOverview(enabled = true) {
  return useQuery<CampaignSchedulesOverviewDto>({
    queryKey: adminKeys.campaignSchedulesOverview(),
    queryFn: () => campaignsApi.getSchedulesOverview(),
    retry: 0,
    enabled,
  });
}

/** GET v1/campaigns/{id} — detalle de una campaña. */
export function useCampaign(id: string | undefined) {
  return useQuery<CampaignDto>({
    queryKey: adminKeys.campaign(id ?? ''),
    queryFn: () => campaignsApi.getById(id as string),
    enabled: Boolean(id),
    retry: 0,
  });
}

/** POST v1/campaigns — crea el borrador (wizard). */
export function useCreateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignDto, unknown, CreateCampaignBody>({
    mutationFn: (body) => campaignsApi.create(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/** PATCH v1/campaigns/{id} — actualiza el borrador. */
export function useUpdateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignDto, unknown, { id: string; body: UpdateCampaignBody }>({
    mutationFn: ({ id, body }) => campaignsApi.update(id, body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] });
      // 🔴 Bug UAT 20-ago-2026 — editar la campaña puede cambiar si está activa PARA UN PAÍS
      // (`horariosPorPais`, fechas, estado), y quien resuelve eso es
      // `recognitionKeys.activeCampaign(ou)` = ['recognition','campaign','active', ou]. Es otro
      // namespace que ['recognition','campaigns'], así que no se invalidaba: con `staleTime` de 5 min
      // (main.tsx) el administrador guardaba un horario, se iba a la pantalla del empleado y seguía
      // viendo la respuesta vieja. Parecía que la tabla de horarios no hacía nada.
      // Se invalida el PREFIJO, sin la OU: la campaña alcanza a muchas y no se sabe aquí cuáles
      // están en caché.
      qc.invalidateQueries({ queryKey: ['recognition', 'campaign', 'active'] });
    },
  });
}

/** POST v1/campaigns/{id}/documents — añade un documento. */
export function useAddCampaignDocument() {
  const qc = useQueryClient();
  return useMutation<CampaignDocumentDto, unknown, { id: string; input: UploadCampaignDocumentInput }>({
    mutationFn: ({ id, input }) => campaignsApi.addDocument(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaign', 'docs'] }),
  });
}

/** DELETE v1/campaigns/documents/{documentId} — elimina un documento. */
export function useDeleteCampaignDocument() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (documentId) => campaignsApi.deleteDocument(documentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaign', 'docs'] }),
  });
}

// ─── RF-079 — documentos PDF corporativos (repositorio por OU, §11) ───

/** GET v1/corporate-documents — listado de administración (DTO completo). */
export function useCorporateDocuments() {
  return useQuery({
    queryKey: recognitionKeysExt.corporateDocs(),
    queryFn: () => userAdminApi.listCorporateDocuments(),
  });
}

/** GET v1/corporate-documents/for-me?lang= — documentos visibles para el usuario (DTO reducido). */
export function useCorporateDocumentsForMe(lang: string, enabled = true) {
  return useQuery({
    queryKey: recognitionKeysExt.corporateDocsForMe(lang),
    queryFn: () => userAdminApi.getCorporateDocumentsForMe(lang),
    enabled,
  });
}

/** POST v1/corporate-documents — sube un PDF (multipart). Invalida el listado. */
export function useUploadCorporateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: UploadCorporateDocumentInput) => userAdminApi.uploadCorporateDocument(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'corporate-docs'] }),
  });
}

/** PUT v1/corporate-documents/{id} — actualiza metadata. Invalida el listado. */
export function useUpdateCorporateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: UpdateCorporateDocumentBody }) =>
      userAdminApi.updateCorporateDocument(id, body),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'corporate-docs'] }),
  });
}

/** DELETE v1/corporate-documents/{id} — baja del documento. Invalida el listado. */
export function useDeleteCorporateDocument() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => userAdminApi.deleteCorporateDocument(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'corporate-docs'] }),
  });
}

/** POST v1/corporate-documents/{id}/files/{lang} — sube el PDF de un idioma (Versión ES/EN). */
export function useUploadCorporateDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lang, file }: { id: string; lang: string; file: File }) =>
      userAdminApi.uploadCorporateDocumentFile(id, lang, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'corporate-docs'] }),
  });
}

/** DELETE v1/corporate-documents/{id}/files/{lang} — quita el PDF de un idioma. */
export function useDeleteCorporateDocumentFile() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, lang }: { id: string; lang: string }) =>
      userAdminApi.deleteCorporateDocumentFile(id, lang),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'corporate-docs'] }),
  });
}

/** POST v1/campaigns/{id}/approval — flujo de aprobación (submit/approve/reject). */
export function useCampaignApproval() {
  const qc = useQueryClient();
  return useMutation<CampaignApprovalDto, unknown, { id: string; action: CampaignApprovalAction; comentario?: string }>({
    mutationFn: ({ id, action, comentario }) => campaignsApi.approval(id, action, comentario),
    // La clave 'campaigns' es prefijo de 'approvals/mine' y de 'approvals/pending': una sola
    // invalidación refresca las dos bandejas, que es justo lo que hace falta al retirar o reenviar.
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/**
 * DELETE v1/campaigns/{id} — borrar un borrador o una pendiente de aprobación (Brillamos). El prefijo
 * 'campaigns' cubre listas, filtros por estado visible y las dos bandejas de aprobación; el detalle
 * de la borrada se descarta para que nadie lea una copia que ya no existe.
 */
export function useDeleteCampaign() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => campaignsApi.deleteCampaign(id),
    onSuccess: (_d, id) => {
      qc.removeQueries({ queryKey: adminKeys.campaign(id) });
      return qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] });
    },
  });
}

/** POST v1/campaigns/{id}/undo-panic — C2/P18-bis: deshace un pánico accidental (24 h, misma OU). */
export function useUndoPanicCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignDto, unknown, string>({
    mutationFn: (id) => campaignsApi.undoPanic(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/** POST v1/campaigns/{id}/panic — botón de pánico (finaliza inmediatamente). */
export function usePanicCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignDto, unknown, string>({
    mutationFn: (id) => campaignsApi.panic(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/** POST v1/campaigns/panic — pánico GLOBAL Brillamos (RF-073): finaliza TODAS las campañas activas. */
export function useGlobalPanic() {
  const qc = useQueryClient();
  return useMutation<{ finalizadas: number }, unknown, void>({
    mutationFn: () => campaignsApi.globalPanic(),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/** POST v1/campaigns/{id}/duplicate — duplica por OU. */
export function useDuplicateCampaign() {
  const qc = useQueryClient();
  return useMutation<CampaignDto, unknown, { id: string; newAlcance?: string[]; asChild?: boolean }>({
    mutationFn: ({ id, newAlcance, asChild }) => campaignsApi.duplicate(id, newAlcance, asChild),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaigns'] }),
  });
}

/** GET v1/campaigns/approvals/pending — aprobaciones pendientes (AdminGlobal). */
export function usePendingApprovals(enabled = true) {
  return useQuery<CampaignApprovalDto[]>({
    queryKey: adminKeys.pendingApprovals(),
    queryFn: () => campaignsApi.getPendingApprovals(),
    enabled,
    retry: 0,
  });
}

/** GET v1/campaigns/approvals/mine — «Mis Solicitudes de Campaña» (RN-C11), en todo estado. */
export function useMyApprovals(enabled = true) {
  return useQuery<CampaignApprovalDto[]>({
    queryKey: adminKeys.myApprovals(),
    queryFn: () => campaignsApi.getMyApprovals(),
    enabled,
    retry: 0,
  });
}

// ─── CPE-138 — Campañas por Localidad (excepciones) ──────────────────────────────

/** GET v1/campaign-exceptions — listado paginado con filtros (programa/baseCampaignId/activa/estado). */
export function useCampaignExceptions(params: CampaignExceptionListParams = {}, enabled = true) {
  return useQuery<PagedResultDto<CampaignExceptionDto>>({
    queryKey: adminKeys.campaignExceptions(params),
    queryFn: () => campaignExceptionsApi.list(params),
    retry: 0,
    enabled,
  });
}

/**
 * GET v1/campaign-exceptions/active-for-me — excepción por localidad que aplica al usuario actual.
 * `data === null` = ninguna aplica (204), que es el caso común y NO es error.
 *
 * Úsalo en pantallas de USUARIO. `useCampaignExceptions` (arriba) es el listado de administración y el
 * backend lo gatea con la capacidad `CrearEditarCampanas`: pedirlo desde una pantalla de colaborador
 * devolvía 403 a todo el que no fuera admin, y con ese 403 la cascada de documentos de Brillamos caía
 * en silencio al nivel de campaña/corporativos en vez de al de la excepción.
 */
export function useActiveCampaignException(programa: CampaignType | undefined, enabled = true) {
  return useQuery<ActiveCampaignExceptionDto | null>({
    queryKey: adminKeys.activeCampaignException(programa ?? ''),
    queryFn: async () => (await campaignExceptionsApi.activeForMe(programa as CampaignType)) ?? null,
    retry: 0,
    enabled: enabled && Boolean(programa),
  });
}

/** GET v1/campaign-exceptions/approvals/pending — bandeja de pendientes (Admin Global). retry 0. */
export function useCampaignExceptionApprovalsPending(enabled = true) {
  return useQuery<CampaignExceptionDto[]>({
    queryKey: adminKeys.campaignExceptionApprovals(),
    queryFn: () => campaignExceptionsApi.approvalsPending(),
    enabled,
    retry: 0,
  });
}

/**
 * POST/PUT v1/campaign-exceptions — crea (sin id) o edita (con id) una excepción. Si el creador no es
 * Admin Global, el backend la deja pendiente + inactiva. Invalida el árbol de excepciones.
 */
export function useSaveCampaignException() {
  const qc = useQueryClient();
  return useMutation<CampaignExceptionDto, unknown, { id?: string; body: CreateCampaignExceptionBody }>({
    mutationFn: ({ id, body }) => (id ? campaignExceptionsApi.update(id, body) : campaignExceptionsApi.create(body)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exceptions'] });
      qc.invalidateQueries({ queryKey: adminKeys.campaignSchedulesOverview() });
    },
  });
}

/** DELETE v1/campaign-exceptions/{id} — baja de una excepción. Invalida el árbol de excepciones. */
export function useDeleteCampaignException() {
  const qc = useQueryClient();
  return useMutation<{ success: boolean }, unknown, string>({
    mutationFn: (id) => campaignExceptionsApi.remove(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exceptions'] });
      qc.invalidateQueries({ queryKey: adminKeys.campaignSchedulesOverview() });
    },
  });
}

/** POST v1/campaign-exceptions/{id}/toggle — enciende/apaga (solo si está aprobada). */
export function useToggleCampaignException() {
  const qc = useQueryClient();
  return useMutation<CampaignExceptionDto, unknown, { id: string; activa: boolean }>({
    mutationFn: ({ id, activa }) => campaignExceptionsApi.toggle(id, activa),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exceptions'] });
      qc.invalidateQueries({ queryKey: adminKeys.campaignSchedulesOverview() });
    },
  });
}

/** POST v1/campaign-exceptions/{id}/approval — resuelve el flujo (submit/approve/reject). */
export function useResolveCampaignExceptionApproval() {
  const qc = useQueryClient();
  return useMutation<CampaignExceptionDto, unknown, { id: string; action: CampaignExceptionApprovalAction; comentario?: string }>({
    mutationFn: ({ id, action, comentario }) => campaignExceptionsApi.approval(id, action, comentario),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exceptions'] });
      qc.invalidateQueries({ queryKey: adminKeys.campaignSchedulesOverview() });
    },
  });
}

// ── Tarea 15 — documentos de excepción (mismo patrón que useCampaignDocuments/useAdd·useDelete
// CampaignDocument, pero contra los 3 endpoints NUEVOS de excepción). El wizard los usa gateado por
// `draft.id` (una excepción sin guardar no tiene id contra el que subir); el visor público
// (`CampaignDocs.tsx`) los usa para resolver el nivel 1 de R8b (excepción con documentos propios).

/**
 * GET v1/campaign-exceptions/{id}/documents — documentos PROPIOS de la excepción.
 * Complementario: si el endpoint aún no está listo NO rompe la landing (retry 0), mismo criterio
 * que `useCampaignDocuments`.
 */
export function useCampaignExceptionDocuments(exceptionId: string | undefined) {
  return useQuery<CampaignExceptionDocumentDto[]>({
    queryKey: recognitionKeys.campaignExceptionDocs(exceptionId ?? ''),
    queryFn: () => campaignExceptionsApi.getDocuments(exceptionId as string),
    enabled: Boolean(exceptionId),
    staleTime: 10 * 60 * 1000,
    retry: 0,
  });
}

/** POST v1/campaign-exceptions/{id}/documents — añade un documento a la excepción. */
export function useAddCampaignExceptionDocument() {
  const qc = useQueryClient();
  return useMutation<CampaignExceptionDocumentDto, unknown, { id: string; input: UploadCampaignExceptionDocumentInput }>({
    mutationFn: ({ id, input }) => campaignExceptionsApi.addDocument(id, input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exception', 'docs'] }),
  });
}

/** DELETE v1/campaign-exceptions/documents/{documentId} — elimina un documento de la excepción. */
export function useDeleteCampaignExceptionDocument() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (documentId) => campaignExceptionsApi.deleteDocument(documentId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['recognition', 'campaign-exception', 'docs'] }),
  });
}

// ─── REQ-134 — Gestión ────────────────────────────────────────────────────────────

/** GET v1/admin/settings — documento de Gestión. */
export function useGestionSettings() {
  return useQuery<GestionSettingsDto>({
    queryKey: adminKeys.gestion(),
    queryFn: () => gestionApi.getSettings(),
    retry: 0,
  });
}

/**
 * WP-07.3 — GET v1/view-settings/banner: banner de Fundamentales (título/subtítulo/logo por idioma)
 * para el hero del landing. No-admin. La vista SIEMPRE cae a i18n/SVG si esto viene vacío o falla
 * (p. ej. endpoint aún no desplegado) → `retry: 0` y sin lanzar; el error se ignora en la vista.
 *
 * QA (2026-08-28) — `placeholderData` conserva la respuesta del idioma anterior mientras llega la
 * del nuevo: cada campo es un `LocalizedText` con TODOS los idiomas ya resueltos (el backend no
 * filtra por `lang`, solo lo usa para otros efectos del mismo endpoint), así que el dato "viejo" ya
 * trae la clave del idioma nuevo. Sin esto, cambiar de idioma volvía la query a `isLoading` un
 * instante y `BrillamosView`/`PerfilView` mostraban de golpe el logo por-defecto (`shineLogoSrc`)
 * antes de recibir de vuelta el logo que el admin sí configuró para ese idioma — un parpadeo
 * visible reportado por QA.
 */
export function useLandingBanner(lang: string) {
  return useQuery<LandingBannerDto>({
    queryKey: ['recognition', 'landing-banner', lang],
    queryFn: () => gestionApi.getLandingBanner(lang),
    retry: 0,
    staleTime: 5 * 60 * 1000,
    placeholderData: (anterior) => anterior,
  });
}

/**
 * GET v1/view-settings/avatar — toggles «Avatar personalizable» y «Mostrar selector post-registro» de
 * Gestión. ANÓNIMO: lo lee el registro Frontline, donde aún no hay sesión. `staleTime` corto para que
 * un cambio del administrador llegue al siguiente registro sin esperar una hora.
 */
export function useAvatarViewSettings() {
  return useQuery<AvatarViewSettingsDto>({
    queryKey: ['recognition', 'avatar-view-settings'],
    queryFn: () => gestionApi.getAvatarViewSettings(),
    retry: 0,
    staleTime: 30 * 1000,
  });
}

/** PUT v1/admin/settings — Guardar (reemplazo atómico). */
export function useSaveGestionSettings() {
  const qc = useQueryClient();
  return useMutation<GestionSettingsDto, unknown, GestionSettingsDto>({
    mutationFn: (body) => gestionApi.saveSettings(body),
    onSuccess: (data) => {
      qc.setQueryData(adminKeys.gestion(), data);
      qc.invalidateQueries({ queryKey: adminKeys.badges() });
      // Bug: "Fundamentales disponibles" (y cualquier otro campo de Gestión que `recognition-rules`
      // proyecta para el colaborador — p. ej. antiSpoiler) tardaba hasta 5 min en reflejarse en
      // Reconocer/Brillamos (`useRecognitionRules`, staleTime: 5 min) porque nada invalidaba esa
      // query al guardar. Clave PARCIAL (sin campaignId/programa): invalida TODAS las variantes —
      // `['recognition-rules', campaignId, programa]` — sin importar con qué parámetros las pidió
      // cada pantalla.
      qc.invalidateQueries({ queryKey: ['recognition-rules'] });
    },
  });
}

/** GET v1/admin/program-settings/{programa} — reglas por programa (campos anulables; null=hereda global). */
export function useProgramSettings(programa: string) {
  return useQuery<RecognitionProgramSettingsDto>({
    queryKey: ['recognition', 'program-settings', programa],
    queryFn: () => gestionApi.getProgramSettings(programa),
    retry: 0,
  });
}

/** PUT v1/admin/program-settings/{programa} — Guardar (escritura parcial; null=hereda global). */
export function useSaveProgramSettings(programa: string) {
  const qc = useQueryClient();
  return useMutation<RecognitionProgramSettingsDto, unknown, RecognitionProgramSettingsDto>({
    mutationFn: (body) => gestionApi.saveProgramSettings(programa, body),
    onSuccess: (data) => {
      qc.setQueryData(['recognition', 'program-settings', programa], data);
      // Las reglas EFECTIVAS del frontline dependen del programa → forzar refetch (todas las variantes por campaignId).
      qc.invalidateQueries({ queryKey: ['recognition-rules'] });
    },
  });
}

/**
 * GET v1/winners/badges — catálogo de insignias PARA LA PANTALLA DE GANADORES, SIN filtrar por
 * categoría (Streak + Campaign mezcladas).
 *
 * Distinto de `useAdminBadges`, que pide el endpoint de administración y exige rol `AdminGlobal`.
 * Aquí basta la capacidad de lectura de Ganadores, que es la que tienen Admin OU/BU y Admin Local
 * País.
 *
 * 🔴 Úsese SOLO para resolver el nombre de la insignia de un ganador YA otorgada (columna de la
 * tabla): un ganador real puede tener una insignia de RACHA, no solo de campaña, y hace falta el
 * catálogo completo para no mostrarle el id crudo. Para ofrecer/administrar insignias — el selector
 * de asignación y "Insignias de Campaña" — usar `useCampaignBadges()`, que filtra a Campaign.
 */
const winnerBadgesKey = ['recognition', 'winners', 'badges'];

export function useWinnerBadges() {
  return useQuery<AdminBadgeDto[]>({
    queryKey: winnerBadgesKey,
    queryFn: () => winnersApi.getBadges(),
    staleTime: 5 * 60 * 1000,
    retry: 0,
  });
}

/**
 * GET v1/winners/badges?categoria=Campaign — catálogo de insignias FILTRADO a Campaign.
 *
 * Lo usan el selector de asignación del cajón de edición y "Insignias de Campaña" (grid + modal):
 * ninguno de los dos debe poder ofrecer ni listar una insignia de racha (esa se otorga sola, nunca
 * se asigna a mano ni se administra desde aquí).
 *
 * La clave va ANIDADA bajo `winnerBadgesKey` a propósito: las mutaciones de insignias de campaña
 * (crear/editar/borrar/subir ícono, más abajo) invalidan `winnerBadgesKey` sin `exact`, y
 * `invalidateQueries` hace match por PREFIJO — esa invalidación alcanza también esta clave sin
 * duplicar la llamada en cada mutación.
 */
const campaignBadgesKey = [...winnerBadgesKey, 'campaign'];

export function useCampaignBadges(enabled = true) {
  return useQuery<AdminBadgeDto[]>({
    queryKey: campaignBadgesKey,
    queryFn: () => winnersApi.getBadges('Campaign'),
    staleTime: 5 * 60 * 1000,
    retry: 0,
    // El `enabled` lo añade la fila 40: el Wrapped consulta este catálogo para comprobar el año de la
    // medalla, y solo debe pedirlo si el slide de ganador está encendido. Los llamadores de admin no
    // pasan nada y siguen igual.
    enabled,
  });
}

/** POST v1/winners/badges — crea una insignia de campaña; invalida el catálogo de Ganadores. */
export function useCreateCampaignBadge() {
  const queryClient = useQueryClient();
  return useMutation<AdminBadgeDto, unknown, AdminBadgeDto>({
    mutationFn: (badge) => winnersApi.createBadge(badge),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: winnerBadgesKey }),
  });
}

/** PUT v1/winners/badges/{id} — edita una insignia de campaña; invalida el catálogo de Ganadores. */
export function useUpdateCampaignBadge() {
  const queryClient = useQueryClient();
  return useMutation<AdminBadgeDto, unknown, { id: string; badge: AdminBadgeDto }>({
    mutationFn: ({ id, badge }) => winnersApi.updateBadge(id, badge),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: winnerBadgesKey }),
  });
}

/** DELETE v1/winners/badges/{id} — borra una insignia de campaña; invalida el catálogo de Ganadores. */
export function useDeleteCampaignBadge() {
  const queryClient = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => winnersApi.deleteBadge(id),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: winnerBadgesKey }),
  });
}

/** POST v1/winners/badges/{id}/image — sube el ícono; invalida el catálogo de Ganadores. */
export function useUploadCampaignBadgeImage() {
  const queryClient = useQueryClient();
  return useMutation<AdminBadgeDto, unknown, { badgeId: string; file: File }>({
    mutationFn: ({ badgeId, file }) => winnersApi.uploadBadgeImage(badgeId, file),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: winnerBadgesKey }),
  });
}

/** GET v1/admin/settings/badges — catálogo de insignias. */
export function useAdminBadges() {
  return useQuery<AdminBadgeDto[]>({
    queryKey: adminKeys.badges(),
    queryFn: () => gestionApi.getBadges(),
    retry: 0,
  });
}

/** PUT v1/admin/settings/badges — edita una insignia. */
export function useSaveBadge() {
  const qc = useQueryClient();
  return useMutation<AdminBadgeDto, unknown, AdminBadgeDto>({
    mutationFn: (badge) => gestionApi.saveBadge(badge),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.badges() }),
  });
}

/** PUT v1/admin/settings/streak-freeze — mínimo semanal, freeze global y apagado de racha. */
export function useSaveStreakSettings() {
  const qc = useQueryClient();
  return useMutation<{ success?: boolean }, unknown, StreakSettingsBody>({
    mutationFn: (body) => gestionApi.saveStreakSettings(body),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.gestion() }),
  });
}

/** POST v1/admin/settings/badges/{id}/image — sube el ícono; invalida el catálogo de insignias. */
export function useUploadBadgeImage() {
  const qc = useQueryClient();
  return useMutation<AdminBadgeDto, unknown, { badgeId: string; file: File }>({
    mutationFn: ({ badgeId, file }) => gestionApi.uploadBadgeImage(badgeId, file),
    onSuccess: () => qc.invalidateQueries({ queryKey: adminKeys.badges() }),
  });
}

// ─── Recap anual (Wrapped) ───────────────────────────────────────────────────────

/** GET v1/admin/recap — historial de ediciones. */
export function useRecapEditions() {
  return useQuery<RecapEditionSummaryDto[]>({
    queryKey: adminKeys.recapEditions(),
    queryFn: () => recapApi.getEditions(),
    retry: 0,
  });
}

/** Una configuración del Recap por id (`recapConfigId`; no dispara sin id). */
export function useRecapEdition(id: string | undefined) {
  return useQuery<RecapEditionDto>({
    queryKey: adminKeys.recapEdition(id ?? ''),
    queryFn: () => recapApi.getEdition(id!),
    enabled: Boolean(id),
    retry: 0,
  });
}

/**
 * POST v1/admin/recap/config/{id}/slide-background (o por año, si no hay id) — sube el fondo de un
 * slide. No invalida nada: la URL se guarda en el borrador y llega al servidor con el Guardar.
 */
export function useUploadRecapSlideBackground() {
  return useMutation<RecapSlideBackgroundDto, unknown, { anio: number; configId?: string | null; file: File }>({
    mutationFn: ({ anio, configId, file }) => recapApi.uploadSlideBackground(anio, file, configId),
  });
}

/**
 * DELETE v1/admin/recap/config/{id} — elimina (borrado lógico) una configuración inactiva y sin
 * notificar. Saca su detalle de la caché e invalida el historial (que también alimenta el selector).
 */
export function useDeleteRecapEdition() {
  const qc = useQueryClient();
  return useMutation<void, unknown, string>({
    mutationFn: (id) => recapApi.deleteEdition(id),
    onSuccess: (_data, id) => {
      qc.removeQueries({ queryKey: adminKeys.recapEdition(id) });
      qc.invalidateQueries({ queryKey: adminKeys.recapEditions() });
    },
  });
}

/** Guardar la configuración del cuerpo (por su id; reemplazo atómico). */
export function useSaveRecapEdition() {
  const qc = useQueryClient();
  return useMutation<RecapEditionDto, unknown, RecapEditionDto>({
    mutationFn: (body) => recapApi.saveEdition(recapConfigId(body), body),
    onSuccess: (data) => {
      qc.setQueryData(adminKeys.recapEdition(recapConfigId(data)), data);
      qc.invalidateQueries({ queryKey: adminKeys.recapEditions() });
    },
  });
}

/** POST v1/admin/recap — nueva configuración (otra del mismo año si ya existe una). */
export function useCreateRecapEdition() {
  const qc = useQueryClient();
  return useMutation<RecapEditionDto, unknown, { anio: number; nombre?: string }>({
    mutationFn: ({ anio, nombre }) => recapApi.createEdition(anio, nombre),
    onSuccess: (data) => {
      qc.setQueryData(adminKeys.recapEdition(recapConfigId(data)), data);
      qc.invalidateQueries({ queryKey: adminKeys.recapEditions() });
    },
  });
}

/** Difusión del aviso al alcance de UNA configuración (por id). */
export function useNotifyRecapEdition() {
  const qc = useQueryClient();
  return useMutation<RecapEditionDto, unknown, { id: string; subOus: string[] }>({
    mutationFn: ({ id, subOus }) => recapApi.notify(id, subOus),
    onSuccess: (data) => {
      qc.setQueryData(adminKeys.recapEdition(recapConfigId(data)), data);
      qc.invalidateQueries({ queryKey: adminKeys.recapEditions() });
    },
  });
}

// ─── RF-130 — El Recap del USUARIO FINAL (lo que gobierna el overlay) ────────────

/**
 * GET v1/recap/current?ouId=&anio= — edición vigente para el colaborador. `undefined` (204) ⇒ no hay
 * ninguna vigente para su OU (y su año, si se pasa) en este momento, y entonces NO hay overlay que
 * pintar: los slides son la configuración, no un adorno sobre datos que existirían igual.
 *
 * RF-130 (criterio 1) — `anio` acota la búsqueda al año que ya resolvió la disponibilidad, para que
 * la EDICIÓN pintada (fondo, título, slides, mensaje genérico) sea del mismo año que los DATOS que se
 * muestran. Sin él (llamador viejo), el backend acota al rango que Analytics sirve.
 *
 * Los slides vienen ya filtrados por `activo` y ordenados por `indice` desde el backend: el
 * consumidor NO los re-filtra ni los re-ordena.
 *
 * `retry: 0` como el resto del módulo: si el servicio no responde, estado de error honesto.
 */
export function useCurrentRecap(ouId?: string, anio?: number, enabled = true) {
  return useQuery<RecapEditionDto | undefined>({
    queryKey: adminKeys.recapCurrent(ouId ?? '', anio),
    queryFn: () => recapApi.getCurrent(ouId, anio),
    enabled,
    retry: 0,
  });
}

/**
 * GET v1/recap/availability — RF-130: la decisión completa de disponibilidad del Wrapped, tomada
 * server-side. Es el ÚNICO gate válido del Wrapped: `!!wrapped.data` no lo es, porque el handler del
 * Wrapped devuelve siempre un DTO (con ceros) y esa condición es constante `true`.
 *
 * Sin parámetros: el titular y su OU salen del token.
 */
export function useWrappedAvailability(enabled = true) {
  return useQuery<WrappedAvailabilityDto>({
    queryKey: adminKeys.wrappedAvailability(),
    queryFn: () => recapApi.getAvailability(),
    enabled,
    retry: 0,
  });
}
